import { Room, createLocalAudioTrack, createLocalVideoTrack, Track, RemoteTrack } from 'livekit-client';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  saveAiInterviewTakeState,
  resolveAiInterviewTakeState,
  saveAiInterviewTakeMinimalBackup,
} from '../../lib/publicAssessmentFlow';
import { usePreventBack } from '../../hooks/usePreventBack';
import { useScreeningLeaveWarning } from '../../hooks/useScreeningLeaveWarning';
import { useProctoring } from '../../hooks/useProctoring';
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { RoomContext } from '@livekit/components-react';
import { useLivekitConnection } from '../../contexts/LivekitConnectionContext';
import { API_BASE_URL } from '../../lib/axiosConfig';
import { AgentAudioVisualizerAura } from '../../components/ui/agent-audio-visualizer-aura';
import { InterviewerAvatar, useAgentSpeaking, useAgentVoiceGender } from '../../components/agents-ui/interviewer-avatar';
import { AgentControlBar } from '../../components/agents-ui/agent-control-bar';
import { ProctoringIndicator } from '../../components/proctoring/ProctoringIndicator';
import { useStableControlBarState } from '../../hooks/agents-ui/use-stable-control-bar-state';
import { AgentChatTranscript } from '../../components/agents-ui/agent-chat-transcript';
import logger from '../../utils/logger';
import { useScreeningSessionEndBeacon } from '../../hooks/useScreeningSessionEndBeacon';
import LeaveScreeningModal from '../../components/ui/LeaveScreeningModal';
import {
  isLikelyInterviewAgentParticipant,
  shouldAttachRemoteInterviewerAudioTrack,
} from '../../utils/livekitInterviewAgent';
import { createAgentLossGuard } from '../../utils/agentLossGuard';
import {
  requestInterviewToken,
  AdmissionCancelledError,
  AdmissionFailedError,
} from '../../utils/interviewAdmission';
import InterviewWaitingLobby from '../../components/interview/InterviewWaitingLobby';
import { useCandidateLanguage } from '../../i18n/candidate';

/**
 * Display-only translation of an error/warning message. Most messages are fixed sentences (the
 * dictionary key); the reconnect failure is built as "<reason>. Please refresh the page.", so that
 * one is translated in two parts. English output is unchanged.
 */
const REFRESH_SUFFIX = '. Please refresh the page.';
function translateInterviewMessage(t, msg) {
  if (typeof msg !== 'string') return msg;
  const direct = t(msg);
  if (direct !== msg) return direct;
  if (msg.endsWith(REFRESH_SUFFIX) && msg.length > REFRESH_SUFFIX.length) {
    const reason = msg.slice(0, -REFRESH_SUFFIX.length);
    return t('{reason}. Please refresh the page.', { reason: t(reason) });
  }
  return msg;
}

/**
 * Normalize message text to always be a string
 * Handles objects, null, undefined, and strings
 * This ensures React can safely render all message text
 */
function normalizeMessageText(text) {
  if (text === null || text === undefined) {
    return '';
  }
  if (typeof text === 'object') {
    // If text is an object, extract text.text if present
    if (text.text !== undefined && text.text !== null) {
      return String(text.text);
    }
    // If no text.text, try to stringify the object (fallback)
    return String(text);
  }
  // Already a string or can be converted to string
  // Ensure we preserve the original text content
  const normalized = String(text);
  // Return empty string only if truly empty, preserve whitespace if meaningful
  return normalized;
}

/**
 * LiveKit Interview Frontend Component
 *
 * Features:
 * - Shows AI speaking vs listening state
 * - Displays live transcript (via LiveKit lk.transcription text stream)
 * - Handles reconnects cleanly
 * - Prevents mute abuse
 * - Handles permission denial gracefully
 */
export default function AIInterviewTakeLivekit({
  location: propLocation,
  preConnectedRoom: propPreConnectedRoom,
}) {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const routeParams = useParams();

  // Support both route-based (normal) and prop-based (seamless) usage
  const location = propLocation || routeLocation;
  const { state: routerState } = location;
  const { sharingLink } = routeParams;
  const [searchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();

  const state = useMemo(
    () => resolveAiInterviewTakeState(sharingLink, routerState, searchParams),
    [sharingLink, routerState, searchParamsKey]
  );

  const advancedType = state?.interview?.advancedType || null;

  useEffect(() => {
    if (state?.sessionId && sharingLink) {
      saveAiInterviewTakeState(sharingLink, state);
    }
  }, [sharingLink, state]);

  useEffect(() => {
    if (sharingLink && state?.email && state?.name) {
      saveAiInterviewTakeMinimalBackup(sharingLink, {
        email: state.email,
        name: state.name,
        screeningId: state.screeningId,
      });
    }
  }, [sharingLink, state?.email, state?.name, state?.screeningId]);

  const takeUrlSyncedRef = useRef(false);
  useEffect(() => {
    takeUrlSyncedRef.current = false;
  }, [sharingLink]);

  useEffect(() => {
    if (!sharingLink || !state?.sessionId || !state?.email || !state?.name) return;
    if (searchParams.get('email') && searchParams.get('name')) return;
    if (takeUrlSyncedRef.current) return;
    takeUrlSyncedRef.current = true;
    const q = new URLSearchParams();
    q.set('email', state.email);
    q.set('name', state.name);
    if (state.screeningId) q.set('screeningId', state.screeningId);
    navigate(`${routeLocation.pathname}?${q.toString()}`, { replace: true, state });
  }, [
    sharingLink,
    state?.sessionId,
    state?.email,
    state?.name,
    state?.screeningId,
    searchParamsKey,
    navigate,
    routeLocation.pathname,
  ]);

  const [showLeaveModal, setShowLeaveModal] = useState(false);
  usePreventBack(!!state?.screeningId, { onBackAttempt: useCallback(() => setShowLeaveModal(true), []) });
  useScreeningSessionEndBeacon(state?.screeningId, state?.email || '');
  useScreeningLeaveWarning(!!state?.screeningId);

  const interviewType = state?.interview?.type || 'voice';
  const isVideoInterview = interviewType === 'video';
  const localVideoRef = useRef(null);
  const localVideoTrackRef = useRef(null);

  // Try to use context, but don't require it (for seamless flow)
  let getRoom, getUrl, getToken, contextAgentReady;
  try {
    const context = useLivekitConnection();
    getRoom = context?.getRoom;
    getUrl = context?.getUrl;
    getToken = context?.getToken;
    contextAgentReady = context?.agentReady;
  } catch (e) {
    // Context not available - use props/state instead
    getRoom = () => propPreConnectedRoom || state?.preConnectedRoom || null;
    getUrl = () => state?.livekitUrl || null;
    getToken = () => state?.livekitToken || null;
    contextAgentReady = state?.agentAlreadyReady || false;
  }

  // 🔴 FIX: Extract stable values from state to prevent unnecessary re-initialization
  // Only re-initialize if these actual inputs change, not if state object reference changes
  const stateKey = useMemo(() => {
    return JSON.stringify({
      name: state?.name,
      email: state?.email,
      sessionId: state?.sessionId,
      sharingLink: state?.sharingLink,
    });
  }, [state?.name, state?.email, state?.sessionId, state?.sharingLink]);

  // State
  const [error, setError] = useState(null);
  /**
   * Waiting room before the room token is issued: null, or
   * { mode: 'waiting', position, estimatedWaitMinutes } while every interviewer is busy,
   * { mode: 'connecting' } while the request is being retried,
   * { mode: 'failed', message } when it gave up (shows "Try again").
   */
  const [admission, setAdmission] = useState(null);
  const pageUnmountedRef = useRef(false);
  useEffect(() => {
    pageUnmountedRef.current = false;
    return () => {
      pageUnmountedRef.current = true;
    };
  }, []);
  /** POST /session/livekit-token through the lobby (see utils/interviewAdmission). */
  const fetchRoomToken = useCallback(async (body, headers) => {
    const data = await requestInterviewToken({
      url: `${API_BASE_URL}/api/ai-interviews/session/livekit-token`,
      headers,
      body,
      onWaiting: (lobby) => setAdmission(lobby ? { mode: 'waiting', ...lobby } : null),
      // A retry blip while in line keeps the line view; otherwise show "connecting".
      onConnecting: () =>
        setAdmission((prev) => (prev?.mode === 'waiting' ? prev : { mode: 'connecting' })),
      isCancelled: () => pageUnmountedRef.current,
    });
    setAdmission(null);
    return data;
  }, []);
  /** Agent non-fatal warnings (e.g. NO_MIC_DETECTED) — must not block the whole interview UI */
  const [agentBannerWarning, setAgentBannerWarning] = useState(null);
  const [status, setStatus] = useState('Connecting...');
  const t = useCandidateLanguage({
    screeningId: state?.screeningId,
    interviewId: state?.interview?._id || state?.interviewId,
  });
  const [isConnected, setIsConnected] = useState(false);
  const [isAISpeaking, setIsAISpeaking] = useState(false);
  const [interviewerAvatar, setInterviewerAvatar] = useState(false); // org feature ai_interview_avatar
  const [isListening, setIsListening] = useState(false);
  const [isMicMuted, setIsMicMuted] = useState(false);
  // Proper transcript store: array of message objects with role, text, timestamp, isFinal, isStreaming flags
  // Every message stays permanently - streaming messages are updated in place until finalized
  // RECONNECT RESILIENT: Transcript state persists across reconnects (React state preservation)
  // Messages are never cleared on speech end, new question, or VAD interruption
  const [transcript, setTranscript] = useState([]); // Full transcript store - persists across reconnects
  const [partialTranscript, setPartialTranscript] = useState(''); // Live partial transcript (candidate) - for display only
  const [partialAITranscript, setPartialAITranscript] = useState(''); // Live partial transcript (AI) - for display only

  // Track agent audio track ID for robust identification
  const agentAudioTrackIdRef = useRef(null);
  const processedSegmentIdsRef = useRef(new Set()); // Track processed segments to avoid duplicates
  // Track last partial transcript role and segment to detect new messages
  const lastPartialRoleRef = useRef(null);
  const lastPartialSegmentIdRef = useRef(null);
  // Track streaming message indices for updating in place
  const streamingMessageIndexRef = useRef({ candidate: null, ai: null });
  // Track current active utterance ID for single bubble model
  const currentUtteranceIdRef = useRef(null);
  // Refs to read partial values in async handlers
  const partialTranscriptRef = useRef('');
  const partialAITranscriptRef = useRef('');
  // Track previous AI speaking state to detect state changes
  const previousAISpeakingRef = useRef(false);
  const [completed, setCompleted] = useState(false);
  const [micPermissionDenied, setMicPermissionDenied] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // For processing state visualization
  const [elapsedTime, setElapsedTime] = useState(0); // Timer
  const [interviewStartTime] = useState(Date.now()); // Start time for timer
  const [isChatPanelVisible, setIsChatPanelVisible] = useState(false); // Right-side conversation panel (hidden by default; toggle to show)
  const [isUserScrolling, setIsUserScrolling] = useState(false); // For transcript scroll detection
  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false); // Track if user scrolled up

  useEffect(() => {
    if (error) setAgentBannerWarning(null);
  }, [error]);

  useEffect(() => {
    if (
      transcript.some((t) => t.role === 'candidate' && String(t.text || '').trim().length > 2)
    ) {
      setAgentBannerWarning(null);
    }
  }, [transcript]);

  useEffect(() => {
    if (partialTranscript?.trim()?.length > 2) setAgentBannerWarning(null);
  }, [partialTranscript]);

  // Initialize proctoring
  const { proctoringEvents, isFullscreen, isRequestingFullscreen, requestFullscreen, addProctoringEvent, shiftClickCount, rightClickCount } = useProctoring(
    state?.sessionId,
    state?.interview?._id || state?.interviewId || sharingLink // Use interview._id from state if available
  );

  const questionIndexRef = useRef(0);
  const lastQuestionEndedAtRef = useRef(0);
  const lastQuestionEndTimeRef = useRef(null);
  const awaitingResponseForQuestionIndexRef = useRef(null);
  const prevIsAISpeakingRef = useRef(false);
  const lastAnswerQuestionIndexRef = useRef(null);
  const lastAnswerQuestionIndexUntilRef = useRef(0);
  const answerWordCountRef = useRef({}); // Track word counts per question for proctoring
  const audioVADStateRef = useRef({
    agentSpeaking: false,
    agentSilenceStart: null,
    candidateSpeaking: false,
    candidateSpeechStartPendingAt: null,
    agentNoiseFloor: 0.001,
    candidateNoiseFloor: 0.001,
  });

  useEffect(() => {
    let cancelled = false;
    let agentAudioCtx;
    let agentAnalyser;
    let agentSource;
    let candidateAudioCtx;
    let candidateAnalyser;
    let candidateSource;
    let timer;
    let warnedAgent = false;
    let warnedCandidate = false;

    const setupAgent = () => {
      const el = agentAudioElementRef.current;
      if (!el) {
        return false;
      }
      if (!el.srcObject && typeof el.captureStream !== 'function') {
        return false;
      }

      const stream = el.srcObject || el.captureStream();
      if (!stream) {
        return false;
      }

      agentAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
      agentAnalyser = agentAudioCtx.createAnalyser();
      agentAnalyser.fftSize = 2048;
      agentSource = agentAudioCtx.createMediaStreamSource(stream);
      const gain = agentAudioCtx.createGain();
      gain.gain.value = 0;
      const sink = agentAudioCtx.createMediaStreamDestination();
      agentSource.connect(agentAnalyser);
      agentAnalyser.connect(gain);
      gain.connect(sink);
      return true;
    };

    const setupCandidate = () => {
      const track = micTrackRef.current;
      const mst =
        track?.mediaStreamTrack ||
        track?._mediaStreamTrack ||
        track?.track?.mediaStreamTrack ||
        track?.track?._mediaStreamTrack;
      if (!mst) return false;

      const stream = new MediaStream([mst]);
      candidateAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
      candidateAnalyser = candidateAudioCtx.createAnalyser();
      candidateAnalyser.fftSize = 2048;
      candidateSource = candidateAudioCtx.createMediaStreamSource(stream);
      const gain = candidateAudioCtx.createGain();
      gain.gain.value = 0;
      const sink = candidateAudioCtx.createMediaStreamDestination();
      candidateSource.connect(candidateAnalyser);
      candidateAnalyser.connect(gain);
      gain.connect(sink);
      return true;
    };

    const rmsFromAnalyser = (analyser) => {
      const buf = new Uint8Array(analyser.fftSize);
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      return Math.sqrt(sum / buf.length);
    };

    const startLoop = () => {
      if (timer) return;
      timer = window.setInterval(() => {
        if (cancelled) return;

        const st = audioVADStateRef.current;
        const now = Date.now();

        const agentThreshold = Math.max(0.002, (st.agentNoiseFloor || 0.001) * 3);
        const candidateThreshold = Math.max(0.002, (st.candidateNoiseFloor || 0.001) * 3);

        if (agentAnalyser) {
          const rms = rmsFromAnalyser(agentAnalyser);
          const speaking = rms > agentThreshold;
          if (speaking) {
            // If agent starts speaking while we still have an open candidate segment,
            // close it immediately to avoid leaving start-only metrics.
            if (
              Number.isFinite(Number(st.candidateSpeechStartTime)) &&
              Number.isFinite(Number(st.candidateActiveQuestionIndex))
            ) {
              const prevStart = Number(st.candidateSpeechStartTime);
              const prevQ = Number(st.candidateActiveQuestionIndex);
              if (prevStart > 0 && prevQ >= 0) {
                const forcedEndTime = now;
                const forcedDurationMs = Math.max(0, forcedEndTime - prevStart);
                addProctoringEvent('CANDIDATE_SPEECH_ENDED', {
                  questionIndex: prevQ,
                  speechEndTime: forcedEndTime,
                  speechDurationMs: forcedDurationMs,
                });
              }
              // Keep the last answer question index alive briefly so late STT finals can still
              // be attributed to the right answer for wordCount.
              lastAnswerQuestionIndexRef.current = prevQ;
              lastAnswerQuestionIndexUntilRef.current = now + 15000;
              st.candidateSpeaking = false;
              st.candidateSilenceStart = null;
              st.candidateSpeechStartTime = null;
              st.candidateActiveQuestionIndex = null;
              st.candidateSpeechStartPendingAt = null;
            }
            st.agentSpeaking = true;
            st.agentSilenceStart = null;
          } else {
            // Update noise floor slowly when not speaking
            st.agentNoiseFloor = st.agentNoiseFloor * 0.98 + rms * 0.02;
          }

          if (!speaking && st.agentSpeaking) {
            if (!st.agentSilenceStart) st.agentSilenceStart = now;
            if (now - st.agentSilenceStart > 1000) {
              st.agentSpeaking = false;
              st.agentSilenceStart = null;

              const questionEndTime = now;
              lastQuestionEndTimeRef.current = questionEndTime;
              if (questionEndTime - (lastQuestionEndedAtRef.current || 0) > 1500) {
                const qIdx = questionIndexRef.current;
                lastQuestionEndedAtRef.current = questionEndTime;
                awaitingResponseForQuestionIndexRef.current = qIdx;

                addProctoringEvent('QUESTION_ENDED', {
                  questionIndex: qIdx,
                  questionEndTime,
                });



                // Advance to next question index for proctoring tagging.
                // This does NOT drive the interview UI; it only labels metrics.
                questionIndexRef.current = qIdx + 1;
              }
              // Don't force-reset candidate state here; candidate may still be finishing an answer.
              // Candidate segment is closed via silence detection or when agent starts speaking again.
            }
          }
        }

        if (candidateAnalyser) {
          const rms = rmsFromAnalyser(candidateAnalyser);
          const speaking = rms > candidateThreshold;

          if (!speaking) {
            st.candidateNoiseFloor = st.candidateNoiseFloor * 0.98 + rms * 0.02;
          }

          if (
            speaking &&
            !st.candidateSpeaking &&
            awaitingResponseForQuestionIndexRef.current != null &&
            lastQuestionEndTimeRef.current
          ) {
            if (!st.candidateSpeechStartPendingAt) st.candidateSpeechStartPendingAt = now;
            if (now - st.candidateSpeechStartPendingAt > 350) {
              st.candidateSpeaking = true;
              st.candidateSpeechStartPendingAt = null;

              // If we still have an open segment from a previous detection (e.g. missed end due
              // to brief pauses/noise), close it now to avoid fragmented metrics.
              if (
                Number.isFinite(Number(st.candidateSpeechStartTime)) &&
                Number.isFinite(Number(st.candidateActiveQuestionIndex))
              ) {
                const prevStart = Number(st.candidateSpeechStartTime);
                const prevQ = Number(st.candidateActiveQuestionIndex);
                if (prevStart > 0 && prevQ >= 0) {
                  const forcedEndTime = now;
                  const forcedDurationMs = Math.max(0, forcedEndTime - prevStart);
                  addProctoringEvent('CANDIDATE_SPEECH_ENDED', {
                    questionIndex: prevQ,
                    speechEndTime: forcedEndTime,
                    speechDurationMs: forcedDurationMs,
                  });
                }
              }

              st.candidateSpeechStartTime = now;
              st.candidateActiveQuestionIndex = awaitingResponseForQuestionIndexRef.current;
              st.candidateSilenceStart = null;

              const questionIndex = awaitingResponseForQuestionIndexRef.current;
              const speechStartTime = now;
              const questionEndTime = lastQuestionEndTimeRef.current;
              const responseDelayMs = Math.max(0, speechStartTime - questionEndTime);

              awaitingResponseForQuestionIndexRef.current = null;

              audioVADStateRef.current.currentAnswerQuestionIndex = questionIndex;
              lastAnswerQuestionIndexRef.current = questionIndex;
              lastAnswerQuestionIndexUntilRef.current = now + 15000;

              addProctoringEvent('CANDIDATE_SPEECH_STARTED', {
                questionIndex,
                speechStartTime,
              });

              addProctoringEvent('RESPONSE_DELAY_METRIC', {
                questionIndex,
                questionEndTime,
                speechStartTime,
                responseDelayMs,
              });


            }
          }

          if (speaking && st.candidateSpeaking) {
            st.candidateSilenceStart = null;
          }

          if (!speaking) {
            st.candidateSpeechStartPendingAt = null;

            if (st.candidateSpeaking) {
              if (!st.candidateSilenceStart) st.candidateSilenceStart = now;

              // Hangover to avoid chopping words: end after 900ms of silence
              if (now - st.candidateSilenceStart > 900) {
                const questionIndex = st.candidateActiveQuestionIndex;
                const speechEndTime = now;
                const startTime = Number(st.candidateSpeechStartTime);

                if (Number.isFinite(questionIndex) && Number.isFinite(startTime) && startTime > 0) {
                  const speechDurationMs = Math.max(0, speechEndTime - startTime);
                  addProctoringEvent('CANDIDATE_SPEECH_ENDED', {
                    questionIndex,
                    speechEndTime,
                    speechDurationMs,
                  });
                }

                // Keep qIdx for a grace window for late transcript finals
                lastAnswerQuestionIndexRef.current = questionIndex;
                lastAnswerQuestionIndexUntilRef.current = now + 15000;

                st.candidateSpeaking = false;
                st.candidateSilenceStart = null;
                st.candidateSpeechStartTime = null;
                st.candidateActiveQuestionIndex = null;
              }
            } else {
              st.candidateSilenceStart = null;
            }
          }
        }
      }, 200);
    };

    const tryInit = () => {
      if (cancelled) return;
      const okAgent = agentAnalyser || setupAgent();
      const okCand = candidateAnalyser || setupCandidate();

      if (!okAgent && !warnedAgent) {
        warnedAgent = true;
      }
      if (!okCand && !warnedCandidate) {
        warnedCandidate = true;
      }

      try {
        if (agentAudioCtx && agentAudioCtx.state === 'suspended') {
          agentAudioCtx.resume().catch(() => { });
        }
      } catch { }
      try {
        if (candidateAudioCtx && candidateAudioCtx.state === 'suspended') {
          candidateAudioCtx.resume().catch(() => { });
        }
      } catch { }
      if (okAgent || okCand) startLoop();
    };

    tryInit();
    const retry = window.setInterval(tryInit, 1000);

    const resumeOnGesture = () => {
      try {
        if (agentAudioCtx && agentAudioCtx.state === 'suspended') {
          agentAudioCtx.resume().catch(() => { });
        }
      } catch { }
      try {
        if (candidateAudioCtx && candidateAudioCtx.state === 'suspended') {
          candidateAudioCtx.resume().catch(() => { });
        }
      } catch { }
    };

    document.addEventListener('click', resumeOnGesture, { capture: true });
    document.addEventListener('keydown', resumeOnGesture, { capture: true });

    return () => {
      cancelled = true;
      if (retry) window.clearInterval(retry);
      if (timer) window.clearInterval(timer);
      document.removeEventListener('click', resumeOnGesture, { capture: true });
      document.removeEventListener('keydown', resumeOnGesture, { capture: true });

      // Flush final speech END if candidate was mid-speech when unmounting
      const st = audioVADStateRef.current;
      if (st.candidateSpeaking && st.candidateSpeechStartTime) {
        const speechEndTime = Date.now();
        const startTime = Number(st.candidateSpeechStartTime);
        const questionIndex = st.candidateActiveQuestionIndex;
        if (Number.isFinite(questionIndex) && Number.isFinite(startTime) && startTime > 0) {
          const speechDurationMs = Math.max(0, speechEndTime - startTime);
          addProctoringEvent('CANDIDATE_SPEECH_ENDED', {
            questionIndex,
            speechEndTime,
            speechDurationMs,
          });
        }
        st.candidateSpeaking = false;
        st.candidateSpeechStartTime = null;
        st.candidateActiveQuestionIndex = null;
      }

      try {
        agentSource && agentSource.disconnect();
        agentAnalyser && agentAnalyser.disconnect();
      } catch { }
      try {
        candidateSource && candidateSource.disconnect();
        candidateAnalyser && candidateAnalyser.disconnect();
      } catch { }
      try {
        agentAudioCtx && agentAudioCtx.close();
      } catch { }
      try {
        candidateAudioCtx && candidateAudioCtx.close();
      } catch { }
    };
  }, [addProctoringEvent]);

  // Request fullscreen immediately when component mounts
  useEffect(() => {
    // Delay slightly to ensure DOM is ready
    const timer = setTimeout(() => {
      requestFullscreen();
      addProctoringEvent('INTERVIEW_SCREEN_ENTERED', {
        timestamp: new Date().toISOString(),
        autoFullscreen: true
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [requestFullscreen, addProctoringEvent]);

  const [isReconnectingUi, setIsReconnectingUi] = useState(false);
  const [agentJoinOverdue, setAgentJoinOverdue] = useState(false); // Agent didn't join in time — show "Keep waiting" instead of hard error
  // Agent vanished *mid*-interview (deploy, OOM, crash) and we're waiting for a
  // replacement. Distinct from agentJoinOverdue, which is about the initial join
  // — the copy for someone ten minutes in has to say something different.
  const [agentLostMidInterview, setAgentLostMidInterview] = useState(false);
  const [agentAudioNotReceived, setAgentAudioNotReceived] = useState(false); // No RTP from agent after 10s — hint to check agent TTS
  const [isInterviewReady, setInterviewReady] = useState(false); // True when agent has joined or is speaking; drives preparing overlay visibility

  // Stable control bar state - prevents flickering
  const stableControlBarState = useStableControlBarState({
    isConnected,
    isReconnecting: isReconnectingUi,
    isAISpeaking,
    isListening,
    isCallEnded: completed,
  });

  // Refs for unified chat scrolling
  const userTranscriptScrollRef = useRef(null);
  const lastScrollTopRef = useRef(0);
  const autoScrollTimeoutRef = useRef(null);

  // Refs
  const roomRef = useRef(null);
  const micTrackRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const RECONNECT_FAILED_MESSAGE =
    'Failed to reconnect after multiple attempts. Please refresh the page.';
  const reconnectTimeoutRef = useRef(null);
  const isReconnectingRef = useRef(false); // Guard to prevent concurrent reconnection attempts
  const muteAbuseTimerRef = useRef(null);
  const agentReadyTimeoutRef = useRef(null);
  const agentJoinTimeoutRef = useRef(null); // Timeout to detect if agent never joins
  const aiFinalizeTimeoutRef = useRef(null); // Timeout for finalizing AI transcript messages
  const completionStatusPollStartedRef = useRef(false); // Prevent duplicate polling intervals
  const muteAbuseCountRef = useRef(0);
  const lastUnmuteTimeRef = useRef(Date.now());
  // Refs to track state without triggering useEffect re-runs
  const completedRef = useRef(false);
  const isConnectedRef = useRef(false);
  // Refs for reconnection (store values needed for reconnect)
  const sessionIdRef = useRef(null);
  const sessionTokenRef = useRef(state?.sessionToken || null);
  const candidateNameRef = useRef('Candidate');
  const urlRef = useRef(null);
  const tokenRef = useRef(null);
  // Track if we're past initial connection phase (to avoid status conflicts)
  const pastConnectionPhaseRef = useRef(false);
  // Track if agent is ready (has joined and sent ready ready signal)
  const agentReadyRef = useRef(false);
  // Track if agent has joined (even if not ready yet)
  const agentJoinedRef = useRef(false);
  // Connection health monitoring
  const connectionHealthRef = useRef({ isConnected: false, lastCheck: Date.now() });
  // Transcription monitoring
  const transcriptionHealthRef = useRef({
    lastTranscriptTime: Date.now(),
    isHealthy: true,
    timeoutId: null,
  });
  // Ref to store handleReconnect function to break circular dependency
  const handleReconnectRef = useRef(null);
  // Guard to prevent double initialization (React StrictMode runs effects twice)
  const initializingRef = useRef(false);
  const initializedRef = useRef(false);
  // Track the stateKey that was used for initialization to detect if it changed
  const initializedStateKeyRef = useRef(null);
  // Track if room.connect() is in progress (cleanup should not set error if we were aborted)
  const connectingRef = useRef(false);
  // Track if mic track is already published (prevents double publishing)
  const micPublishedRef = useRef(false);
  // Promise resolver for waiting for connection state
  const connectionReadyResolverRef = useRef(null);
  // Agent TTS audio element (must be in DOM for playback in many browsers)
  const agentAudioElementRef = useRef(null);
  // Agent audio track for visualizer
  const agentAudioTrackRef = useRef(null);

  const navigateToCompletion = useCallback(() => {
    const completionParams = new URLSearchParams();
    if (state?.screeningId) completionParams.set('screeningId', state.screeningId);
    if (state?.interview?.type) completionParams.set('interviewType', state.interview.type);
    const paramsString = completionParams.toString();
    const completionPath = `/interview-complete${paramsString ? `?${paramsString}` : ''}`;
    navigate(completionPath, {
      replace: true,
      state: {
        sessionId: sessionIdRef.current,
        sessionToken: sessionTokenRef.current,
        candidateName: candidateNameRef.current,
        hasAssessment: state?.hasAssessment,
        screeningId: state?.screeningId,
        email: state?.email,
        interviewType: state?.interview?.type,
      },
    });
  }, [navigate, state]);

  // Keep a stable handle on navigateToCompletion so the guard below can be
  // constructed once without capturing a stale closure.
  const navigateToCompletionRef = useRef(navigateToCompletion);
  navigateToCompletionRef.current = navigateToCompletion;

  /**
   * Guard for the interviewer leaving mid-interview (deploy, OOM, crash).
   * Timing rules and rationale live in utils/agentLossGuard.js, where they are
   * unit-tested; this only wires them to the page.
   */
  const agentLossGuardRef = useRef(null);
  if (!agentLossGuardRef.current) {
    agentLossGuardRef.current = createAgentLossGuard({
      isCompleted: () => completedRef.current,

      onLost: () => {
        logger.log('Agent left mid-interview — holding the room and requesting a replacement');
        agentJoinedRef.current = false;
        agentReadyRef.current = false;
        setAgentLostMidInterview(true);
        setIsAISpeaking(false);
        setStatus('Reconnecting to your interviewer...');
        // Deliberately NOT setInterviewReady(false): the backend-completion
        // poll's effect depends on it, and its cleanup would clear the interval
        // — which `completionStatusPollStartedRef` then prevents restarting.
        // That poll is the 4s fallback for a missed "completed" signal, and it
        // must keep running through a reconnect. The overlay is driven by
        // agentLostMidInterview instead (see showPreparingOverlay).
      },

      // Best-effort nudge. LiveKit may re-dispatch on its own and the grace
      // window runs regardless, so a failure here is not fatal — which also
      // means this works against a server predating the ensure-agent endpoint.
      onRequestAgent: () => {
        const sid = sessionIdRef.current;
        const stok = sessionTokenRef.current;
        if (!sid || !stok) return;
        fetch(`${API_BASE_URL}/api/ai-interviews/session/${sid}/ensure-agent`, {
          method: 'POST',
          headers: { 'x-session-token': stok },
        }).catch(() => { });
      },

      onRecovered: (elapsedMs) => {
        logger.log(`Replacement interviewer joined after ${elapsedMs}ms`);
        setAgentLostMidInterview(false);
      },

      // Nobody came. Fall back to the previous behaviour: close the interview
      // out on whatever transcript was persisted.
      onGiveUp: () => {
        logger.log('No replacement interviewer within the grace window — closing out');
        setAgentLostMidInterview(false);
        setStatus('Interview completed!');
        setCompleted(true);
        completedRef.current = true;
        setIsAISpeaking(false);
        setIsListening(false);
        navigateToCompletionRef.current?.();
      },
    });
  }

  const clearAgentLostGrace = useCallback(() => {
    agentLossGuardRef.current?.agentJoined();
  }, []);

  // Status string used when the interview session is ready (agent joined)
  const INTERVIEW_READY_STATUS = 'Ready! The interview will begin shortly...';
  // Overlay hides when isInterviewReady is true (set on agent join, agent audio track, or first AI transcript)
  // agentLostMidInterview is its own term rather than flipping isInterviewReady,
  // which would tear down the backend-completion poll (see onLost above).
  const showPreparingOverlay = !completed && (!isInterviewReady || agentLostMidInterview);

  // Log agent prompt data to browser console when we have session (e.g. from PreTest)
  useEffect(() => {
    const sid = state?.sessionId;
    const stok = state?.sessionToken;
    if (!sid || !stok || typeof window === 'undefined') return;
    fetch(`${API_BASE_URL}/api/ai-interviews/session/${sid}/agent-data`, {
      headers: { 'x-session-token': stok },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) {

        }
      })
      .catch(() => { });
  }, [state?.sessionId, state?.sessionToken]);

  // ---------------------------------------------------------------------------
  // Completion fallback:
  // If the LiveKit agent "completed" data message is missed, use backend
  // session status to force navigation to the completion page.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isInterviewReady) return;
    if (completedRef.current) return;
    if (completionStatusPollStartedRef.current) return;

    completionStatusPollStartedRef.current = true;

    const maxPollMs = 180000; // 3 minutes

    const poll = async () => {
      if (completedRef.current) return;
      const sid = sessionIdRef.current;
      const stok = sessionTokenRef.current;
      if (!sid || !stok) return;

      try {
        const r = await fetch(`${API_BASE_URL}/api/ai-interviews/session/${sid}/agent-data`, {
          headers: { 'x-session-token': stok },
        });
        if (!r.ok) return;
        const data = await r.json();
        const backendStatus = data?.session?.status;
        if (backendStatus !== 'completed') return;

        // Close on backend "completed" even if we missed LiveKit data signal.
        completedRef.current = true;
        setStatus('Interview completed!');
        setCompleted(true);
        setIsAISpeaking(false);
        setIsListening(false);

        navigateToCompletion();
      } catch {
        // Best-effort polling only
      }
    };

    const intervalId = setInterval(poll, 4000);
    const timeoutId = setTimeout(() => clearInterval(intervalId), maxPollMs);

    // First poll immediately
    poll();

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [isInterviewReady, navigateToCompletion]);

  // ---------------------------------------------------------------------------
  // Prevent accidental navigation during interview (back button, tab close)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Warn before closing tab/window
    const handleBeforeUnload = (e) => {
      if (!completedRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    // Block back button during interview by pushing a state entry
    const blockBackNavigation = () => {
      if (!completedRef.current) {
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', blockBackNavigation);

    // Push initial state so popstate can catch back button
    window.history.pushState(null, '', window.location.href);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', blockBackNavigation);
    };
  }, []);

  /**
   * Map component state flags to AgentAudioVisualizerAura state enum
   * Priority: processing > AI speaking > listening > connecting
   * Note: partialTranscript is only used as a fallback when explicitly listening
   */
  const getVisualizerState = useCallback(() => {
    if (!isConnected) return 'connecting';
    if (isProcessing) return 'thinking';
    if (isAISpeaking) return 'speaking';
    if (isListening) return 'listening';
    // Only use partialTranscript as a fallback when we're actually listening (not when AI is speaking)
    if (partialTranscript && !isAISpeaking && !isProcessing) return 'listening';
    return 'connecting';
  }, [isConnected, isProcessing, isAISpeaking, isListening, partialTranscript]);

  // Avatar shows only once the agent has said which voice it is using; until then, the aura.
  const avatarGender = useAgentVoiceGender(isConnected ? roomRef.current : null, interviewerAvatar);
  const agentSpeaking = useAgentSpeaking(avatarGender ? roomRef.current : null);
  const avatarSpeaking = agentSpeaking ?? getVisualizerState() === 'speaking';

  /**
   * Validate LiveKit room connection health
   * Checks: room connected, has remote participants (agent), agent has audio track
   */
  const validateRoomConnection = useCallback(() => {
    if (!roomRef.current) return false;

    const roomState = roomRef.current.state;
    const isConnected = roomState === 'connected';
    // Check for any remote participant (agent uses various identity patterns)
    const remoteParticipants = roomRef.current.remoteParticipants;
    const hasRemote = remoteParticipants && remoteParticipants.size > 0;
    // Check if any remote has audio track (agent publishes TTS audio)
    let hasRemoteAudio = false;
    if (hasRemote) {
      for (const [, p] of remoteParticipants) {
        if (p.audioTrackPublications.size > 0) {
          hasRemoteAudio = true;
          break;
        }
      }
    }

    const isHealthy = isConnected && hasRemote;
    if (connectionHealthRef.current) {
      connectionHealthRef.current = {
        isConnected: isHealthy,
        hasRemoteAudio,
        lastCheck: Date.now(),
      };
    }

    return isHealthy;
  }, []);

  /**
   * Monitor transcription health and detect timeouts
   * Increased timeout to 30s to avoid false alarms during natural pauses
   * (AI thinking, TTS generation, question transitions, etc.)
   */
  const monitorTranscriptionHealth = useCallback(() => {
    const now = Date.now();
    const timeSinceLastTranscript =
      now - (transcriptionHealthRef.current?.lastTranscriptTime || now);

    // Reset timeout if we have recent transcript (within 30s)
    if (timeSinceLastTranscript < 30000) {
      if (transcriptionHealthRef.current?.timeoutId) {
        clearTimeout(transcriptionHealthRef.current.timeoutId);
        transcriptionHealthRef.current.timeoutId = null;
      }
      if (transcriptionHealthRef.current) {
        transcriptionHealthRef.current.isHealthy = true;
      }
      return;
    }

    // Set timeout warning if no transcript for 30+ seconds
    if (
      !transcriptionHealthRef.current?.timeoutId &&
      timeSinceLastTranscript >= 30000 &&
      transcriptionHealthRef.current
    ) {
      transcriptionHealthRef.current.timeoutId = setTimeout(() => {
        if (transcriptionHealthRef.current) {
          transcriptionHealthRef.current.isHealthy = false;
        }
      }, 5000);
    }
  }, []);

  /**
   * Handle microphone permission denial
   */
  const handlePermissionDenial = useCallback(() => {
    setMicPermissionDenied(true);
    setAgentBannerWarning(null);
    setError(
      'Microphone access is required for this interview. Please enable microphone permissions and refresh the page.'
    );
  }, []);

  /**
   * Request microphone permission
   */
  const requestMicrophonePermission = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop the stream immediately - we just needed permission
      stream.getTracks().forEach((track) => track.stop());
      setMicPermissionDenied(false);
      return true;
    } catch (err) {
      handlePermissionDenial();
      return false;
    }
  }, [handlePermissionDenial]);

  /**
   * Prevent mute abuse (detect rapid mute/unmute)
   */
  const checkMuteAbuse = useCallback(() => {
    const now = Date.now();
    const timeSinceLastUnmute = now - lastUnmuteTimeRef.current;

    // If unmuted within 2 seconds of last unmute, count as abuse
    if (timeSinceLastUnmute < 2000) {
      muteAbuseCountRef.current += 1;

      if (muteAbuseCountRef.current >= 3) {
        setError(
          'Please keep your microphone enabled during the interview. Excessive muting is not allowed.'
        );
        // Force unmute
        if (micTrackRef.current) {
          micTrackRef.current.unmute();
          setIsMicMuted(false);
        }
        muteAbuseCountRef.current = 0;
      }
    } else {
      muteAbuseCountRef.current = 0;
    }

    lastUnmuteTimeRef.current = now;
  }, []);

  /**
   * Handle mute toggle (with abuse prevention)
   * Uses track.enabled instead of mute/unmute to avoid track lifecycle issues
   */
  const handleMuteToggle = useCallback(() => {
    if (!micTrackRef.current) return;

    if (isMicMuted) {
      // Unmuting - check for abuse
      checkMuteAbuse();
      micTrackRef.current.unmute();
      setIsMicMuted(false);
    } else {
      // Muting - warn but allow (interviewer may need to mute for privacy)
      micTrackRef.current.mute();
      setIsMicMuted(true);
    }
  }, [isMicMuted, checkMuteAbuse]);

  /** Extended wait when agent hasn't joined in 60s — user chose "Keep waiting" (90s more). */
  const AGENT_JOIN_KEEP_WAITING_MS = 90000;
  const handleAgentJoinKeepWaiting = useCallback(() => {
    setAgentJoinOverdue(false);
    if (agentJoinTimeoutRef.current) {
      clearTimeout(agentJoinTimeoutRef.current);
      agentJoinTimeoutRef.current = null;
    }
    agentJoinTimeoutRef.current = setTimeout(() => {
      if (!agentJoinedRef.current && !completedRef.current) {
        setAgentJoinOverdue(true);
      }
    }, AGENT_JOIN_KEEP_WAITING_MS);
  }, []);

  /**
   * Cleanup function
   * @param {boolean} isUnmount - If true, fully stop tracks. If false, only disable (for reconnects)
   */
  const cleanup = useCallback((isUnmount = false) => {
    // 🔴 FIX: Delay cleanup during initialization to prevent race conditions
    // This protects against edge cases where cleanup is called while initialization is in progress
    // (e.g., from navigation or state changes, even though StrictMode is disabled)
    if (initializingRef.current && !isUnmount) {
      logger.log('⏸️ Cleanup requested during initialization - delaying...');
      setTimeout(() => {
        // Only cleanup if initialization finished or was cancelled
        if (!initializingRef.current) {
          cleanup(isUnmount);
        }
      }, 100);
      return;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Reset reconnection guard
    if (isUnmount) {
      isReconnectingRef.current = false;
    }

    if (aiFinalizeTimeoutRef.current) {
      clearTimeout(aiFinalizeTimeoutRef.current);
      aiFinalizeTimeoutRef.current = null;
    }

    if (muteAbuseTimerRef.current) {
      clearInterval(muteAbuseTimerRef.current);
      muteAbuseTimerRef.current = null;
    }

    if (agentReadyTimeoutRef.current) {
      clearTimeout(agentReadyTimeoutRef.current);
      agentReadyTimeoutRef.current = null;
    }

    // Clear agent join timeout
    if (agentJoinTimeoutRef.current) {
      clearTimeout(agentJoinTimeoutRef.current);
      agentJoinTimeoutRef.current = null;
    }

    // Drop the mid-interview agent-lost grace window. Left running past
    // unmount it would navigate a candidate who has already moved on.
    agentLossGuardRef.current?.cancel();

    // 🔴 FIX: Mic track lifecycle management
    // - During reconnect: disable track (don't stop) to prevent CLIENT_INITIATED disconnects
    // - On unmount: stop track completely
    if (micTrackRef.current) {
      if (isUnmount) {
        micTrackRef.current.stop();
        micTrackRef.current = null;
      } else {
        micTrackRef.current.mute();
      }
    }

    if (localVideoTrackRef.current && isUnmount) {
      localVideoTrackRef.current.stop();
      localVideoTrackRef.current = null;
    }

    // Remove agent TTS audio element from DOM (we appended it for playback)
    if (agentAudioElementRef.current && document.body.contains(agentAudioElementRef.current)) {
      try {
        document.body.removeChild(agentAudioElementRef.current);
      } catch (_) { }
      agentAudioElementRef.current = null;
    }

    // Clear agent audio track ref
    agentAudioTrackRef.current = null;

    // Clear health monitoring timeouts
    if (transcriptionHealthRef.current.timeoutId) {
      clearTimeout(transcriptionHealthRef.current.timeoutId);
      transcriptionHealthRef.current.timeoutId = null;
    }

    if (roomRef.current) {
      roomRef.current.disconnect();
      roomRef.current = null;
    }
  }, []);

  /**
   * Safely publish microphone track - only when room is fully connected
   * This prevents "could not createOffer with closed peer connection" errors
   */
  const publishMicrophoneTrack = useCallback(async (room) => {
    // Helper function to actually perform the publish
    const doPublish = async (room) => {
      // Double-check state before publishing
      if (room.state !== 'connected') {
        throw new Error(`Cannot publish - room state is "${room.state}", expected "connected"`);
      }

      if (!micTrackRef.current) {
        throw new Error('Cannot publish - mic track not created');
      }

      try {
        logger.log('🎤 Publishing microphone track...');
        await room.localParticipant.publishTrack(micTrackRef.current, {
          dtx: false,
          red: true,
        });
        micPublishedRef.current = true;
        logger.log('✅ Microphone track published successfully');

        // 🔍 DEBUG: Verify track publication
        logger.log('🔍 DEBUG: Track publication status:', {
          trackSid: micTrackRef.current.sid,
          trackKind: micTrackRef.current.kind,
          trackMuted: micTrackRef.current.isMuted,
          roomState: room.state,
          publicationsCount: room.localParticipant.audioTrackPublications.size,
          publications: Array.from(room.localParticipant.audioTrackPublications.values()).map(
            (p) => ({
              trackSid: p.trackSid,
              kind: p.kind,
              source: p.source,
              isMuted: p.isMuted,
            })
          ),
        });
      } catch (err) {
        micPublishedRef.current = false; // Reset flag on error so we can retry
        throw err;
      }
    };

    // Guard: Don't publish if already published (check both flag and actual publication)
    if (micPublishedRef.current) {
      // Double-check actual publication status
      const existingPublication = Array.from(
        room.localParticipant.audioTrackPublications.values()
      ).find(
        (pub) => pub.track === micTrackRef.current || pub.trackSid === micTrackRef.current?.sid
      );
      if (existingPublication) {
        logger.log('⏭️ Mic track already published to room, skipping');
        return;
      } else {
        // Flag says published but track not found - reset flag and continue
        micPublishedRef.current = false;
      }
    }

    // Guard: Don't publish if mic track doesn't exist
    if (!micTrackRef.current) {
      return;
    }

    // Guard: Ensure track is not muted before publishing
    if (micTrackRef.current.isMuted) {
      logger.log('🔧 Unmuting mic track before publishing');
      micTrackRef.current.unmute();
    }

    // Guard: Wait for room to be fully connected before publishing
    if (room.state !== 'connected') {


      // Wait for connection state to become "connected"
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          room.off('connectionStateChanged', checkState);
          reject(new Error(`Timeout waiting for room to connect. Current state: ${room.state}`));
        }, 10000); // 10 second timeout

        const checkState = (state) => {
          logger.log(`🔗 Connection state changed to: ${state}`);
          if (state === 'connected') {
            clearTimeout(timeout);
            room.off('connectionStateChanged', checkState);
            // Now try to publish (room is connected)
            doPublish(room).then(resolve).catch(reject);
          } else if (state === 'disconnected') {
            clearTimeout(timeout);
            room.off('connectionStateChanged', checkState);
            reject(new Error('Room disconnected while waiting for connection'));
          }
        };

        room.on('connectionStateChanged', checkState);
      });
    }

    // Room is connected, publish now
    return doPublish(room);
  }, []);

  // ---------------------------------------------------------------------------
  // Device-interruption recovery (e.g. an incoming phone call on mobile)
  //
  // When the OS hands a phone call to the foreground, the browser ends or mutes
  // the candidate's microphone MediaStreamTrack and suspends audio playback. The
  // LiveKit room stays "connected", so no reconnect fires — but the mic is dead
  // (the agent hears nothing) and TTS playback is paused (the candidate hears
  // nothing), and neither side recovers on its own. These helpers re-acquire the
  // mic and resume playback when the track dies or when the candidate returns to
  // the tab. There is a matching re-armable watchdog on the agent (livekit-agent)
  // that pings us via a RECOVER_AUDIO data signal as a backstop.
  // ---------------------------------------------------------------------------
  const micRestartInProgressRef = useRef(false);
  const restartMicrophoneRef = useRef(null);

  const buildMicConstraints = useCallback(() => {
    const micConstraints = {
      echoCancellation: true,
      noiseSuppression: false,
      autoGainControl: false,
    };
    const selectedMicrophoneId = state?.selectedMicrophoneId;
    if (selectedMicrophoneId && selectedMicrophoneId !== 'default') {
      micConstraints.deviceId = { exact: selectedMicrophoneId };
    }
    return micConstraints;
  }, [state]);

  /**
   * Resume agent TTS playback after the page/audio was suspended (tab hidden,
   * phone call, autoplay block). Safe to call repeatedly.
   */
  const resumeAgentAudioPlayback = useCallback(() => {
    const room = roomRef.current;
    try { room?.startAudio?.().catch(() => { }); } catch { }
    try {
      const el = agentAudioElementRef.current;
      if (el) {
        el.muted = false;
        el.volume = 1.0;
        el.play().catch(() => { });
      }
    } catch { }
  }, []);

  /** True when the local mic track is missing or its underlying device track is dead/suspended. */
  const isMicTrackDead = useCallback(() => {
    const mic = micTrackRef.current;
    const mst = mic?.mediaStreamTrack;
    return !mic || !mst || mst.readyState === 'ended' || mst.muted === true;
  }, []);

  /**
   * Attach device-loss listeners to a freshly created local mic track. When the
   * OS ends the track (phone call, device unplug) we re-acquire it. A native
   * `mute` (an OS interruption that may auto-recover) is debounced: we only
   * restart if it has not unmuted within a few seconds.
   */
  const attachMicTrackRecovery = useCallback((track) => {
    if (!track) return;
    const triggerRestart = (reason) => {
      if (completedRef.current) return;
      logger.log(`🎤 Mic track lost (${reason}) — attempting recovery`);
      if (restartMicrophoneRef.current) restartMicrophoneRef.current(reason);
    };
    try { track.once('ended', () => triggerRestart('track_ended')); } catch { }
    const mst = track.mediaStreamTrack;
    if (mst && typeof mst.addEventListener === 'function') {
      mst.addEventListener('ended', () => triggerRestart('mediastream_ended'), { once: true });
      // OS interruptions (incoming call) usually mute the native track first.
      // Give it a few seconds to auto-unmute; if still muted, force a restart.
      mst.addEventListener('mute', () => {
        setTimeout(() => {
          if (!completedRef.current && micTrackRef.current?.mediaStreamTrack?.muted) {
            triggerRestart('mediastream_muted');
          }
        }, 3000);
      });
    }
  }, []);

  /**
   * Re-acquire the microphone and republish it. Used when the device track dies
   * mid-interview. Guarded against concurrent restarts.
   */
  const restartMicrophone = useCallback(async (reason) => {
    const room = roomRef.current;
    if (!room || completedRef.current) return;
    if (micRestartInProgressRef.current) return;
    micRestartInProgressRef.current = true;
    try {
      logger.log(`🔄 Restarting microphone (reason: ${reason})`);

      // Unpublish + stop the dead track so the new one is the only published mic.
      const old = micTrackRef.current;
      if (old) {
        try { await room.localParticipant.unpublishTrack(old, true); } catch { }
        try { old.stop(); } catch { }
      }
      micTrackRef.current = null;
      micPublishedRef.current = false;

      // Recreate (falling back off an exact deviceId that may no longer exist).
      const constraints = buildMicConstraints();
      let track;
      try {
        track = await createLocalAudioTrack(constraints);
      } catch (err) {
        if ((err.name === 'OverconstrainedError' || err.name === 'NotFoundError') && constraints.deviceId) {
          delete constraints.deviceId;
          track = await createLocalAudioTrack(constraints);
        } else {
          throw err;
        }
      }

      micTrackRef.current = track;
      attachMicTrackRecovery(track);
      setIsMicMuted(false);

      await publishMicrophoneTrack(room);
      setAgentBannerWarning(null);
      logger.log('✅ Microphone re-acquired and republished after device interruption');
    } catch (err) {
      console.error('[MIC_RESTART_FAIL]', err.message);
      setAgentBannerWarning(
        'We lost access to your microphone (this can happen after a phone call). ' +
        'Please tap the screen — and refresh the page if the interviewer still cannot hear you.'
      );
    } finally {
      micRestartInProgressRef.current = false;
    }
  }, [buildMicConstraints, attachMicTrackRecovery, publishMicrophoneTrack]);

  // Expose the latest restartMicrophone via ref so track-event listeners always
  // call the current implementation regardless of when they were attached.
  useEffect(() => {
    restartMicrophoneRef.current = restartMicrophone;
  }, [restartMicrophone]);

  /**
   * Recover audio + mic when the candidate returns to the tab (visibilitychange /
   * pageshow / focus). Resumes suspended TTS playback and, if the mic track died
   * while the page was backgrounded (e.g. during a phone call), re-acquires it.
   */
  useEffect(() => {
    const onMaybeVisible = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      if (completedRef.current || !roomRef.current) return;
      logger.log('👁️ Page visible/focused again — resuming audio and checking mic');
      resumeAgentAudioPlayback();
      if (isMicTrackDead() && restartMicrophoneRef.current) {
        restartMicrophoneRef.current('page_visible_mic_dead');
      }
    };
    document.addEventListener('visibilitychange', onMaybeVisible);
    window.addEventListener('pageshow', onMaybeVisible);
    window.addEventListener('focus', onMaybeVisible);
    return () => {
      document.removeEventListener('visibilitychange', onMaybeVisible);
      window.removeEventListener('pageshow', onMaybeVisible);
      window.removeEventListener('focus', onMaybeVisible);
    };
  }, [resumeAgentAudioPlayback, isMicTrackDead]);

  /**
   * Setup room event handlers
   * Simplified following official LiveKit Agents best practices:
   * - Speaker detection via participant identity (not weighted scoring)
   * - Transcription via native lk.transcription stream
   * - Agent detection via participantConnected (not data channel)
   * - Minimal data channel usage (only "completed" signal)
   */
  const setupRoomHandlers = useCallback((room, sessionId) => {
    // Check for agent already in room
    for (const [identity, participant] of room.remoteParticipants) {
      if (isLikelyInterviewAgentParticipant(participant)) {
        logger.log('Agent already in room:', identity);
        agentJoinedRef.current = true;
        agentReadyRef.current = true;
        setAgentJoinOverdue(false);
        // A replacement arrived — cancel the "close out the interview" fallback.
        clearAgentLostGrace();
        if (agentJoinTimeoutRef.current) {
          clearTimeout(agentJoinTimeoutRef.current);
          agentJoinTimeoutRef.current = null;
        }
        if (agentReadyTimeoutRef.current) {
          clearTimeout(agentReadyTimeoutRef.current);
          agentReadyTimeoutRef.current = null;
        }
        if (!completedRef.current) {
          setInterviewReady(true);
          setStatus(INTERVIEW_READY_STATUS);
        }
      }
    }

    // Log remote participants at setup time
    logger.log(`Remote participants at setup: ${room.remoteParticipants.size}`);

    // Detect when agent joins room (replaces data channel "agentReady")
    room.on('participantConnected', (participant) => {
      logger.log('Participant connected:', participant.identity);
      if (isLikelyInterviewAgentParticipant(participant)) {
        logger.log('Agent joined room');
        agentJoinedRef.current = true;
        agentReadyRef.current = true;
        setAgentJoinOverdue(false);
        // A replacement arrived — cancel the "close out the interview" fallback.
        // The agent's session-resume prompt takes it from here: it reads the
        // persisted transcript and picks up where the previous one left off.
        clearAgentLostGrace();
        if (agentJoinTimeoutRef.current) {
          clearTimeout(agentJoinTimeoutRef.current);
          agentJoinTimeoutRef.current = null;
        }
        if (agentReadyTimeoutRef.current) {
          clearTimeout(agentReadyTimeoutRef.current);
          agentReadyTimeoutRef.current = null;
        }
        if (!completedRef.current) {
          setInterviewReady(true);
          setStatus(INTERVIEW_READY_STATUS);
        }
        // Resume AudioContext for first TTS
        room.startAudio().catch(() => { });
      }
    });

    // Handle audio track subscription -- attach agent audio for playback
    // When we receive the agent's audio track, they are sending audio (speaking) — hide preparing overlay immediately
    room.on('trackSubscribed', (track, publication, participant) => {
      if (track.kind !== 'audio') return;

      const isRemote = participant && participant !== room.localParticipant;
      if (!isRemote) return;
      if (!shouldAttachRemoteInterviewerAudioTrack(participant)) return;

      logger.log(`Audio track subscribed from: ${participant?.identity}`);

      // Agent is sending audio (TTS) — interview is ready; hide overlay even if status/transcript paths didn't fire
      if (!completedRef.current) {
        setInterviewReady(true);
        setStatus(INTERVIEW_READY_STATUS);
      }

      // Resume AudioContext
      room.startAudio().catch(() => { });

      // Reuse existing audio element if present
      if (agentAudioElementRef.current && document.body.contains(agentAudioElementRef.current)) {
        if (agentAudioTrackRef.current && agentAudioTrackRef.current !== track) {
          try {
            agentAudioTrackRef.current.detach(agentAudioElementRef.current);
          } catch { }
        }
        agentAudioTrackRef.current = track;
        track.attach(agentAudioElementRef.current);
        agentAudioElementRef.current.muted = false;
        agentAudioElementRef.current.volume = 1.0;
        agentAudioElementRef.current.play().catch(() => { });
        return;
      }

      // Create new audio element
      agentAudioTrackRef.current = track;
      const audioElement = track.attach();
      if (audioElement) {
        if (!document.body.contains(audioElement)) {
          audioElement.setAttribute('data-livekit-agent-audio', 'true');
          Object.assign(audioElement.style, {
            position: 'fixed',
            left: '-9999px',
            width: '0',
            height: '0',
            opacity: '0',
            pointerEvents: 'none',
          });
          document.body.appendChild(audioElement);
        }
        agentAudioElementRef.current = audioElement;
        audioElement.muted = false;
        audioElement.volume = 1.0;
        audioElement.setAttribute('playsinline', 'true');
        audioElement.setAttribute('autoplay', 'true');
        audioElement.play().catch(() => { });

        track.on('ended', () => {
          agentAudioTrackRef.current = null;
        });
      }
    });

    // Handle audio track unsubscription
    room.on('trackUnsubscribed', (track, publication, participant) => {
      if (track.kind !== 'audio' || !participant || participant === room.localParticipant) return;
      if (!shouldAttachRemoteInterviewerAudioTrack(participant)) return;
      if (agentAudioTrackRef.current === track) {
        agentAudioTrackRef.current = null;
      }
    });

    // -----------------------------------------------------------------------
    // LiveKit native transcription via lk.transcription text stream
    // Clean implementation: speaker detection via participant identity,
    // simple Set-based dedup for final segments.
    // -----------------------------------------------------------------------
    const seenFinalSegments = new Set();
    const seenCandidateWordCountSegments = new Set();
    const seenCandidateTranscriptSegments = new Set();
    // Text-based dedup: catches duplicates that arrive with different segment IDs
    // (known LiveKit bug #3138 where user transcriptions are duplicated with new IDs).
    // Stores { text, role, timestamp } for recent finals within a sliding window.
    const recentFinalTexts = [];
    const TEXT_DEDUP_WINDOW_MS = 5000;
    // Diarization: track unique speakers per question to detect whispering/help
    const speakersByQuestion = {};
    const multiSpeakerFiredForQuestion = new Set();
    // Time window (ms) to merge consecutive same-role segments into one bubble.
    // STT produces multiple FINAL segments per user turn; this keeps them grouped.
    const MERGE_WINDOW_MS = 4000;

    room.registerTextStreamHandler('lk.transcription', async (reader, participantInfo) => {
      try {
        const message = await reader.readAll();
        const attributes = reader.info?.attributes || {};
        const isFinal =
          attributes['lk.transcription_final'] === 'true' ||
          attributes['lk.transcription_final'] === true;
        const segmentId = attributes['lk.segment_id'];

        // Speaker detection: local participant = candidate, remote = AI
        // Some environments don't populate participantInfo.identity reliably for local segments,
        // so we include multiple fallbacks.
        const participantIdentity = participantInfo?.identity || '';
        const localIdentity = room.localParticipant?.identity || '';
        const isLocal =
          participantInfo?.isLocal === true ||
          (participantIdentity && localIdentity && participantIdentity === localIdentity) ||
          (!participantInfo || (!participantIdentity && !!room.localParticipant));
        const role = isLocal ? 'candidate' : 'ai';

        const text = typeof message === 'string' ? message.trim() : String(message || '').trim();
        if (!text) return;

        if (role === 'candidate' && isFinal) {
          const now = Date.now();
          const qIdx =
            audioVADStateRef.current?.currentAnswerQuestionIndex ??
            (now < (lastAnswerQuestionIndexUntilRef.current || 0)
              ? lastAnswerQuestionIndexRef.current
              : null);
          const dedupKey = segmentId ? `${segmentId}-candidate` : null;
          const wordCountAlreadySeen = !!(dedupKey && seenCandidateWordCountSegments.has(dedupKey));
          if (!wordCountAlreadySeen && dedupKey) {
            seenCandidateWordCountSegments.add(dedupKey);
            if (seenCandidateWordCountSegments.size > 200) {
              const arr = Array.from(seenCandidateWordCountSegments);
              seenCandidateWordCountSegments.clear();
              arr.slice(-100).forEach((k) => seenCandidateWordCountSegments.add(k));
            }
          }

          if (qIdx != null) {
            if (!wordCountAlreadySeen) {
              const words = text.split(/\s+/).filter(Boolean);
              const prev = Number(answerWordCountRef.current[qIdx] || 0);
              const next = prev + words.length;
              answerWordCountRef.current[qIdx] = next;
              addProctoringEvent('ANSWER_WORD_COUNT', {
                questionIndex: qIdx,
                wordCount: next,
              });
            }

            const transcriptDedupKey = segmentId ? `${segmentId}-candidate-text` : null;
            if (!transcriptDedupKey || !seenCandidateTranscriptSegments.has(transcriptDedupKey)) {
              if (transcriptDedupKey) {
                seenCandidateTranscriptSegments.add(transcriptDedupKey);
                if (seenCandidateTranscriptSegments.size > 200) {
                  const arr = Array.from(seenCandidateTranscriptSegments);
                  seenCandidateTranscriptSegments.clear();
                  arr.slice(-100).forEach((k) => seenCandidateTranscriptSegments.add(k));
                }
              }
              addProctoringEvent('ANSWER_TRANSCRIPT_SEGMENT_FINAL', {
                questionIndex: qIdx,
                segmentId: segmentId || null,
                text,
              });
            }

            // Diarization-based multi-speaker detection (proctoring only).
            // Deepgram may expose speaker via attributes or prefix text with "Speaker N:".
            // NOTE: In AI interviews, 1 speaker is the AI Agent itself + 1 is Candidate (total 2 expected speakers).
            // We subtract 1 for the AI Agent so multi-speaker violations ONLY trigger when >= 3 total speakers (>= 2 human speakers in room) are detected.
            const spkAttr = attributes['speaker'] || attributes['lk.speaker']
              || attributes['dg.speaker'] || attributes['lk.diarize_speaker'];
            const spkTextMatch = text.match(/^Speaker\s+(\d+)\s*:/i);
            const detectedSpeaker = spkAttr != null ? String(spkAttr) : (spkTextMatch ? spkTextMatch[1] : null);

            if (detectedSpeaker != null) {
              if (!speakersByQuestion[qIdx]) speakersByQuestion[qIdx] = new Set();
              speakersByQuestion[qIdx].add(detectedSpeaker);

              // Subtract 1 for the AI Agent (minimum is 1, so 1 or 2 total speakers => 1 human speaker, never 0)
              const humanSpeakerCount = Math.max(1, speakersByQuestion[qIdx].size >= 2 ? speakersByQuestion[qIdx].size - 1 : 1);

              if (speakersByQuestion[qIdx].size > 2 && !multiSpeakerFiredForQuestion.has(qIdx)) {
                multiSpeakerFiredForQuestion.add(qIdx);
                addProctoringEvent('MULTI_SPEAKER_DETECTED', {
                  questionIndex: qIdx,
                  speakerCount: humanSpeakerCount,
                  totalRawSpeakers: speakersByQuestion[qIdx].size,
                  speakerLabels: Array.from(speakersByQuestion[qIdx]),
                  latestSpeaker: detectedSpeaker,
                });
              }
            }
          }
        }

        // Debug: log every transcript event (remove after debugging)
        logger.log(
          `[transcript] role=${role} final=${isFinal} seg=${segmentId} text="${text.slice(0, 60)}..."`
        );

        // ------------------------------------------------------------------
        // Receiving ANY AI transcript is definitive proof the agent is live.
        // Clear the "agent didn't join" timeout and banner if still showing.
        // Also set ready status so the preparing overlay hides when agent speaks.
        // ------------------------------------------------------------------
        if (role === 'ai') {
          if (!agentJoinedRef.current) {
            logger.log('Agent confirmed via AI transcript - clearing join timeout');
            agentJoinedRef.current = true;
            agentReadyRef.current = true;
            setAgentJoinOverdue(false);
            if (agentJoinTimeoutRef.current) {
              clearTimeout(agentJoinTimeoutRef.current);
              agentJoinTimeoutRef.current = null;
            }
          }
          if (!completedRef.current) {
            setInterviewReady(true);
            setStatus(INTERVIEW_READY_STATUS);
          }
        }

        // ------------------------------------------------------------------
        // Auto-finalize: when one role starts speaking, commit any pending
        // streaming entries from the OTHER role. This is critical because
        // AI transcripts from SyncedTextOutput arrive as interim-only
        // (no final=true event), so they must be finalized when the user
        // starts speaking next.
        // ------------------------------------------------------------------
        const otherRole = role === 'candidate' ? 'ai' : 'candidate';
        setTranscript((prev) => {
          let changed = false;
          const updated = prev.map((msg) => {
            if (msg.role === otherRole && msg.isStreaming && !msg.isFinal) {
              changed = true;
              return { ...msg, isFinal: true, isStreaming: false };
            }
            return msg;
          });
          return changed ? updated : prev;
        });

        // Dedup: skip already-processed final segments (by segment ID)
        if (isFinal && segmentId) {
          const dedupKey = `${segmentId}-${role}`;
          if (seenFinalSegments.has(dedupKey)) return;
          seenFinalSegments.add(dedupKey);
          if (seenFinalSegments.size > 200) {
            const arr = Array.from(seenFinalSegments);
            seenFinalSegments.clear();
            arr.slice(-100).forEach((k) => seenFinalSegments.add(k));
          }
        }

        // Text-based dedup: catches duplicates with different segment IDs
        // (known LiveKit bug #3138). If the same role sent the same text
        // within TEXT_DEDUP_WINDOW_MS, skip it.
        if (isFinal && text) {
          const now = Date.now();
          // Prune old entries outside the window
          while (recentFinalTexts.length > 0 && now - recentFinalTexts[0].ts > TEXT_DEDUP_WINDOW_MS) {
            recentFinalTexts.shift();
          }
          const isDupText = recentFinalTexts.some((entry) => entry.role === role && entry.text === text);
          if (isDupText) {
            logger.log(`[transcript] Skipping text-duplicate: role=${role} text="${text.slice(0, 40)}..."`);
            return;
          }
          recentFinalTexts.push({ role, text, ts: now });
          if (recentFinalTexts.length > 50) {
            recentFinalTexts.splice(0, recentFinalTexts.length - 25);
          }
        }

        if (isFinal) {
          // Update transcription health
          transcriptionHealthRef.current.lastTranscriptTime = Date.now();
          transcriptionHealthRef.current.isHealthy = true;
          monitorTranscriptionHealth();

          // Update AI speaking state based on transcription activity
          if (role === 'ai') {
            setIsAISpeaking(false);
          }

          setTranscript((prev) => {
            // 1. Try to finalize an existing streaming utterance for this role
            const streamingIdx = prev.findIndex(
              (msg) =>
                msg.role === role &&
                !msg.isFinal &&
                (segmentId != null ? msg.segmentId === segmentId : true)
            );
            if (streamingIdx >= 0) {
              const updated = [...prev];
              updated[streamingIdx] = {
                ...updated[streamingIdx],
                text,
                isFinal: true,
                isStreaming: false,
                segmentId: segmentId || updated[streamingIdx].segmentId,
                timestamp: new Date(), // refresh for merge window
              };
              currentUtteranceIdRef.current = null;
              return updated;
            }

            // 2. Merge: append to most recent same-role FINAL entry if within window.
            //    This prevents "broken pieces" when STT produces many short segments.
            let lastSameRoleIdx = -1;
            for (let i = prev.length - 1; i >= 0; i--) {
              if (prev[i].role === role) {
                lastSameRoleIdx = i;
                break;
              }
            }
            if (lastSameRoleIdx >= 0) {
              const lastEntry = prev[lastSameRoleIdx];
              const elapsed = Date.now() - new Date(lastEntry.timestamp).getTime();
              if (lastEntry.isFinal && elapsed < MERGE_WINDOW_MS) {
                // Check the text doesn't already end with this segment
                if (!lastEntry.text.endsWith(text)) {
                  const updated = [...prev];
                  updated[lastSameRoleIdx] = {
                    ...updated[lastSameRoleIdx],
                    text: updated[lastSameRoleIdx].text + ' ' + text,
                    timestamp: new Date(), // keep the merge window sliding
                  };
                  return updated;
                }
                return prev; // duplicate tail
              }
            }

            // 3. Dedup: exact match
            const isDup = prev.some(
              (item) => item.role === role && item.text === text && item.isFinal
            );
            if (isDup) return prev;

            // 4. Add new final message
            return [
              ...prev,
              {
                id: `utterance-${Date.now()}-${role}`,
                role,
                text,
                timestamp: new Date(),
                segmentId: segmentId || null,
                isFinal: true,
                isStreaming: false,
              },
            ];
          });
        } else {
          // Interim: update or create streaming utterance
          if (role === 'ai') {
            setIsAISpeaking(true);
          }

          setTranscript((prev) => {
            // 1. Find existing streaming utterance for this role AND same segment.
            //    Matching on segmentId prevents a new AI response from overwriting
            //    a previous one (each response has a unique segment ID).
            const activeIdx = prev.findIndex(
              (msg) =>
                msg.role === role &&
                msg.isStreaming &&
                !msg.isFinal &&
                (segmentId == null || msg.segmentId == null || msg.segmentId === segmentId)
            );

            if (activeIdx >= 0) {
              // If segment IDs differ, this is a NEW response -- finalize old, create new
              if (
                segmentId &&
                prev[activeIdx].segmentId &&
                prev[activeIdx].segmentId !== segmentId
              ) {
                const updated = [...prev];
                updated[activeIdx] = {
                  ...updated[activeIdx],
                  isFinal: true,
                  isStreaming: false,
                };
                const newUtterance = {
                  id: `utterance-${Date.now()}-${role}`,
                  role,
                  text,
                  timestamp: new Date(),
                  segmentId: segmentId || null,
                  isFinal: false,
                  isStreaming: true,
                };
                currentUtteranceIdRef.current = newUtterance.id;
                return [...updated, newUtterance];
              }

              // Same segment -- update in place
              const updated = [...prev];
              updated[activeIdx] = { ...updated[activeIdx], text, isStreaming: true };
              currentUtteranceIdRef.current = updated[activeIdx].id;
              return updated;
            }

            // 2. Create new streaming utterance
            const newUtterance = {
              id: `utterance-${Date.now()}-${role}`,
              role,
              text,
              timestamp: new Date(),
              segmentId: segmentId || null,
              isFinal: false,
              isStreaming: true,
            };
            currentUtteranceIdRef.current = newUtterance.id;
            return [...prev, newUtterance];
          });

          // Update partial transcript state for live display
          if (role === 'candidate') {
            setPartialTranscript(text);
          } else {
            setPartialAITranscript(text);
          }

          lastPartialRoleRef.current = role;
          lastPartialSegmentIdRef.current = segmentId;
        }
      } catch (err) {
        console.error('[TRANSCRIPT_HANDLER_ERROR]', err.message);
      }
    });

    // -----------------------------------------------------------------------
    // Data channel: handle "completed" and "error" signals from agent
    // -----------------------------------------------------------------------
    room.on('dataReceived', (payload, participant, kind, topic) => {
      let data;
      try {
        const decoder = new TextDecoder();
        data = JSON.parse(decoder.decode(payload));
      } catch {
        return;
      }

      if (data?.type === 'error') {
        const errorCode = data.code || 'UNKNOWN';
        const errorMsg = data.message || 'Something went wrong with the interview. Please refresh the page to try again.';
        console.error('[AGENT_ERROR]', { sessionId: sessionIdRef.current, code: errorCode, message: errorMsg });

        const userMessages = {
          NO_INTERVIEW_DATA: 'We couldn\'t load your interview questions. Please refresh the page to try again.',
          AGENT_AUTH_FAILED: 'The interview could not start due to a configuration error. Please refresh the page to try again.',
          AGENT_INIT_FAILED: 'The interview could not start. Please refresh the page to try again.',
          NO_MIC_DETECTED: 'We can\'t hear your microphone. Please make sure your microphone is enabled and refresh the page.',
        };

        setError(userMessages[errorCode] || errorMsg);
        completedRef.current = true;
        return;
      }

      // Non-fatal warnings from agent (mic issues, STT degraded, etc.)
      // Dismissible banner only — never use setError() here or refresh/kill the interview UI.
      if (data?.type === 'warning') {
        const warningCode = data.code || 'UNKNOWN';
        const warningMsg = data.message || 'There may be an issue with your connection.';
        logger.log(`Agent warning signal received: ${warningCode} - ${warningMsg}`);

        const warningMessages = {
          NO_MIC_DETECTED:
            'We are not picking up speech yet (this can happen right after you reconnect). If you already allowed the mic, try speaking an answer — or dismiss this and continue. Refresh only if the interviewer still cannot hear you.',
          STT_DEGRADED:
            'We\'re having trouble hearing you clearly. Please speak closer to your microphone.',
          RECOVER_AUDIO: 'Re-establishing your audio connection…',
        };

        // Backstop: the agent's re-armable watchdog asks us to re-establish audio
        // when no candidate speech has been heard mid-interview (e.g. the mic died
        // or playback was suspended during a phone call). Validate locally first —
        // resuming playback is always safe; the mic is only restarted if dead.
        if (warningCode === 'RECOVER_AUDIO' || warningCode === 'NO_MIC_DETECTED') {
          resumeAgentAudioPlayback();
          if (isMicTrackDead() && restartMicrophoneRef.current) {
            restartMicrophoneRef.current('server_recover_signal');
          }
        }

        setAgentBannerWarning(warningMessages[warningCode] || warningMsg);
        return;
      }

      if (data?.type === 'completed') {
        logger.log('Interview completed signal received - navigating to completion page');
        completedRef.current = true;
        setStatus('Interview completed!');
        setCompleted(true);
        setIsAISpeaking(false);
        setIsListening(false);
        navigateToCompletion();

        // Safety: if navigate didn't run (e.g. race), retry after 5s
        setTimeout(() => {
          if (completedRef.current && window.location.pathname !== '/interview-complete') {
            logger.log('Safety redirect after completed signal (5s)');
            navigateToCompletion();
          }
        }, 5000);
      }
    });

    // SDK built-in reconnection: show UI feedback while LiveKit auto-recovers (ICE restart / full reconnect)
    room.on('reconnecting', () => {
      if (!completedRef.current) {
        logger.log('LiveKit SDK reconnecting (automatic)...');
        setIsReconnectingUi(true);
        setStatus('Connection interrupted. Reconnecting...');
      }
    });

    room.on('reconnected', () => {
      logger.log('LiveKit SDK reconnected successfully');
      setIsReconnectingUi(false);
      setIsConnected(true);
      isConnectedRef.current = true;
      if (!completedRef.current) {
        setStatus(agentJoinedRef.current ? INTERVIEW_READY_STATUS : 'Reconnected');
      }
    });

    // Handle disconnection -- fires only after SDK built-in reconnection has failed
    room.on('disconnected', (reason) => {
      logger.log('LiveKit disconnected:', reason);
      setIsConnected(false);
      isConnectedRef.current = false;
      setInterviewReady(false);
      try {
        room.unregisterTextStreamHandler('lk.transcription');
      } catch { }

      if (!completedRef.current && reason !== 'CLIENT_INITIATED' && !isReconnectingRef.current) {
        setStatus('Connection lost. Attempting to reconnect...');
        if (
          sessionIdRef.current &&
          urlRef.current &&
          tokenRef.current &&
          handleReconnectRef.current
        ) {
          handleReconnectRef.current(sessionIdRef.current, urlRef.current, tokenRef.current);
        } else {
          setError('Connection lost. Please refresh the page.');
        }
      }
    });

    // Agent left the room.
    //
    // Departure is a WEAK signal for "interview over" — the strong signal is the
    // explicit completion the agent sends when its endInterview tool fires
    // (completedRef). A worker killed by a deploy or an OOM looks identical to a
    // normal goodbye from here, so treat departure as an interruption first and
    // only close out if no replacement arrives. See utils/agentLossGuard.js.
    room.on('participantDisconnected', (participant) => {
      if (isLikelyInterviewAgentParticipant(participant)) {
        if (completedRef.current) {
          logger.log('Agent left room after completion - already navigated');
          return;
        }
        agentLossGuardRef.current?.agentLost();
      }
    });

    // Catch-up: process any already-subscribed remote audio tracks
    for (const [identity, participant] of room.remoteParticipants) {
      for (const pub of participant.trackPublications.values()) {
        if (pub.kind === 'audio' && pub.isSubscribed && pub.track) {
          if (agentAudioTrackRef.current !== pub.track) {
            room.emit('trackSubscribed', pub.track, pub, participant);
          }
        }
      }
    }

    // AudioContext resume fallback for autoplay policy
    const ensureAudioContextResumed = () => {
      room
        .startAudio()
        .then(() => {
          document.removeEventListener('click', ensureAudioContextResumed, { capture: true });
          document.removeEventListener('touchstart', ensureAudioContextResumed, { capture: true });
        })
        .catch(() => { });
    };
    setTimeout(() => {
      if (!agentAudioElementRef.current || agentAudioElementRef.current.paused) {
        document.addEventListener('click', ensureAudioContextResumed, {
          capture: true,
          once: false,
        });
        document.addEventListener('touchstart', ensureAudioContextResumed, {
          capture: true,
          once: false,
        });
        setTimeout(() => {
          document.removeEventListener('click', ensureAudioContextResumed, { capture: true });
          document.removeEventListener('touchstart', ensureAudioContextResumed, { capture: true });
        }, 60000);
      }
    }, 3000);

    // Connection state tracking
    room.on('connectionStateChanged', (state) => {
      if (state === 'connected') {
        setIsConnected(true);
        isConnectedRef.current = true;
        if (!pastConnectionPhaseRef.current) {
          setStatus(
            room.remoteParticipants.size === 0 ? 'Waiting for interviewer to join...' : 'Connected'
          );

          // Log interview start (fullscreen already requested on component mount)
          addProctoringEvent('LIVEKIT_CONNECTED', {
            timestamp: new Date().toISOString(),
            participantCount: room.remoteParticipants.size
          });
        }
        isReconnectingRef.current = false;
      }
    });
  }, []);

  /**
   * Handle reconnection
   * Includes: token refresh, reconnection guard, agent join timeout, and better error handling
   */
  const handleReconnect = useCallback(
    async (sessionId, url, oldToken) => {
      // 🔴 FIX 1: Reconnection guard - prevent concurrent attempts
      if (isReconnectingRef.current) {
        logger.log('⏸️ Reconnection already in progress, skipping duplicate attempt');
        return;
      }

      if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        isReconnectingRef.current = false;
        setError(RECONNECT_FAILED_MESSAGE);
        return;
      }

      isReconnectingRef.current = true;
      setIsReconnectingUi(true);
      reconnectAttemptsRef.current += 1;
      setStatus(
        `Reconnecting... (Attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`
      );

      try {
        // Cleanup old connection
        cleanup();

        // Wait a bit before reconnecting (exponential backoff)
        await new Promise((resolve) => setTimeout(resolve, 1000 * reconnectAttemptsRef.current));

        // 🔴 FIX 2: Refresh token before reconnecting (tokens may expire during reconnection attempts)
        setStatus('Refreshing connection token...');
        let token = oldToken;
        let refreshedUrl = url;

        const fetchToken = async () => {
          const reconnHeaders = { 'Content-Type': 'application/json' };
          if (sessionTokenRef.current) reconnHeaders['x-session-token'] = sessionTokenRef.current;
          const res = await fetch(`${API_BASE_URL}/api/ai-interviews/session/livekit-token`, {
            method: 'POST',
            headers: reconnHeaders,
            body: JSON.stringify({ sessionId }),
          });
          return res;
        };

        try {
          let tokenRes = await fetchToken();
          if (!tokenRes.ok && tokenRes.status >= 500) {
            await new Promise((r) => setTimeout(r, 1000));
            tokenRes = await fetchToken();
          }
          if (tokenRes.ok) {
            const tokenData = await tokenRes.json();
            token = tokenData.token;
            refreshedUrl = tokenData.url;
            setInterviewerAvatar(tokenData.interviewerAvatar === true);

            tokenRef.current = token;
            urlRef.current = refreshedUrl;
            logger.log('✅ Token refreshed for reconnection');
          } else {
            console.error('[TOKEN_REFRESH_FAIL]', { status: tokenRes.status });
          }
        } catch (tokenError) {
          console.error('[TOKEN_REFRESH_FAIL]', tokenError.message);
        }

        // Validate and clean URL format
        if (refreshedUrl && !refreshedUrl.startsWith('wss://')) {
          throw new Error('Invalid LiveKit URL format');
        }

        // Remove /rtc or /ws paths if present (LiveKit SDK adds these automatically)
        if (refreshedUrl) {
          refreshedUrl = refreshedUrl.trim();
          if (refreshedUrl.endsWith('/rtc') || refreshedUrl.endsWith('/rtc/')) {
            refreshedUrl = refreshedUrl.replace(/\/rtc\/?$/, '');
          }
          if (refreshedUrl.endsWith('/ws') || refreshedUrl.endsWith('/ws/')) {
            refreshedUrl = refreshedUrl.replace(/\/ws\/?$/, '');
          }
        }

        // Reconnect with fresh token
        setStatus('Connecting to LiveKit...');
        const room = new Room({
          // CRITICAL: Must enable autoSubscribe on reconnect to receive agent audio
          autoSubscribe: true,
          adaptiveStream: true,
        });
        roomRef.current = room;

        // 🔊 CRITICAL: Register event handlers BEFORE connect to avoid missing track events
        setupRoomHandlers(room, sessionId);

        await room.connect(refreshedUrl, token);
        setIsConnected(true);
        isConnectedRef.current = true;
        pastConnectionPhaseRef.current = true;
        reconnectAttemptsRef.current = 0;
        isReconnectingRef.current = false;
        setIsReconnectingUi(false);
        setStatus('Reconnected!');
        setError(null);
        setAgentBannerWarning(null);

        // 🔊 Resume AudioContext after reconnect so agent audio plays
        try {
          await room.startAudio();
          logger.log('✅ room.startAudio() succeeded after reconnect');
        } catch (audioErr) {
        }

        // 🔴 FIX: Reset mic published flag on reconnect (need to republish)
        micPublishedRef.current = false;

        // Reset agent ready state (will wait for new ready signal)
        agentReadyRef.current = false;
        agentJoinedRef.current = false;

        // 🔴 FIX: Reuse existing microphone track instead of recreating
        // Mic track should be created ONCE per page load, not on every reconnect
        setStatus('Reconnecting microphone...');
        if (!micTrackRef.current) {
          // Only create if track doesn't exist (shouldn't happen, but safety check)
          const micTrack = await createLocalAudioTrack(buildMicConstraints());
          micTrackRef.current = micTrack;
          attachMicTrackRecovery(micTrack);
        } else {
          // Re-enable existing track (was muted during cleanup)
          logger.log('✅ Reusing existing mic track');
          micTrackRef.current.unmute();
        }

        // Publish mic immediately after reconnect
        // 🔴 FIX: Use safe publish function that waits for room to be fully connected
        try {
          await publishMicrophoneTrack(room);
          logger.log('mic published immediately');
        } catch (err) {
          // Don't fail completely - mic might publish later
        }

        // 🔴 FIX 3: CRITICAL - Set up agent join timeout after reconnection
        // This detects if agent service is down and prevents infinite reconnection loop
        if (agentJoinTimeoutRef.current) {
          clearTimeout(agentJoinTimeoutRef.current);
        }

        agentJoinTimeoutRef.current = setTimeout(() => {
          if (!agentJoinedRef.current && !completedRef.current) {

            setAgentJoinOverdue(true);
          }
        }, 60000); // 60 seconds timeout

        // Also set up agent ready timeout (if agent joins but doesn't send ready signal)
        if (agentReadyTimeoutRef.current) {
          clearTimeout(agentReadyTimeoutRef.current);
        }

        agentReadyTimeoutRef.current = setTimeout(() => {
          if (!agentReadyRef.current && !completedRef.current && agentJoinedRef.current) {
            setInterviewReady(true);
            setStatus(INTERVIEW_READY_STATUS);
          }
        }, 30000); // 30 seconds timeout
      } catch (err) {
        console.error('[RECONNECT_FAIL]', { sessionId: sessionIdRef.current, error: err.message });
        isReconnectingRef.current = false;
        setIsReconnectingUi(false);

        let errorMessage = 'Failed to reconnect';
        let shouldRetry = true;

        const msg = err?.message ?? '';
        if (
          msg.includes('token') ||
          msg.includes('expired') ||
          msg.includes('unauthorized')
        ) {
          errorMessage = 'Connection token expired or invalid';
        } else if (
          msg.includes('network') ||
          msg.includes('connection') ||
          msg.includes('timeout')
        ) {
          errorMessage = 'Network connection failed';
        } else if (
          msg.includes('Client initiated disconnect') ||
          msg.includes('Abort') ||
          msg.includes('abort') ||
          msg.includes('closed before the connection is established') ||
          msg.includes('WebSocket')
        ) {
          errorMessage = 'Connection was interrupted';
          // Same transient errors as initial connect — keep retrying
        } else if (msg.includes('URL') || msg.includes('invalid')) {
          errorMessage = 'Invalid server configuration';
          shouldRetry = false;
        } else if (msg.includes('room') || msg.includes('not found')) {
          errorMessage = 'Interview room not found';
          shouldRetry = false;
        }

        if (reconnectAttemptsRef.current < maxReconnectAttempts && shouldRetry) {
          reconnectTimeoutRef.current = setTimeout(() => {
            handleReconnect(sessionId, urlRef.current, tokenRef.current);
          }, 5000);
        } else {
          setError(`${errorMessage}. Please refresh the page.`);
        }
      }
    },
    [cleanup, setupRoomHandlers]
  );

  /** Retry reconnection from the error UI: reset attempts, fetch fresh token, then reconnect */
  const handleRetryReconnect = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    setError(null);
    reconnectAttemptsRef.current = 0;
    isReconnectingRef.current = false;
    setIsReconnectingUi(true);
    setStatus('Getting new connection...');
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (sessionTokenRef.current) headers['x-session-token'] = sessionTokenRef.current;
      let data;
      try {
        data = await fetchRoomToken({ sessionId: sid }, headers);
      } catch (tokenErr) {
        if (tokenErr instanceof AdmissionCancelledError) return;
        setAdmission(null);
        setError(
          tokenErr instanceof AdmissionFailedError
            ? tokenErr.message
            : tokenErr.message || 'Could not get connection. Please refresh the page.'
        );
        setIsReconnectingUi(false);
        return;
      }
      let url = (data.url || '').trim();
      if (url.endsWith('/rtc') || url.endsWith('/rtc/')) url = url.replace(/\/rtc\/?$/, '');
      if (url.endsWith('/ws') || url.endsWith('/ws/')) url = url.replace(/\/ws\/?$/, '');
      tokenRef.current = data.token;
      urlRef.current = url;
      setInterviewerAvatar(data.interviewerAvatar === true);
      await handleReconnect(sid, url, data.token);
    } catch (e) {
      console.error('[RETRY_RECONNECT_FAIL]', { sessionId: sessionIdRef.current, error: e.message });
      setError('Connection failed. Please refresh the page.');
      setIsReconnectingUi(false);
    }
  }, [handleReconnect, fetchRoomToken]);

  // Store handleReconnect in ref so setupRoomHandlers can access it
  useEffect(() => {
    handleReconnectRef.current = handleReconnect;
  }, [handleReconnect]);

  // Keep refs in sync with state (for handlers that use refs)
  useEffect(() => {
    completedRef.current = completed;
  }, [completed]);

  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);

  // Removed isConnectedUi effect - now using stableControlBarState hook
  // The stable hook manages visibility independently and prevents flickering

  useEffect(() => {
    partialTranscriptRef.current = partialTranscript;
  }, [partialTranscript]);
  useEffect(() => {
    partialAITranscriptRef.current = partialAITranscript;
  }, [partialAITranscript]);

  // Timer effect - update elapsed time every second
  useEffect(() => {
    if (completed) return;

    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - interviewStartTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [completed, interviewStartTime]);

  // Health monitoring effect - check connection and transcription health
  useEffect(() => {
    if (completed) return;

    const healthCheckInterval = setInterval(() => {
      // Check connection health (only log if actually degraded, not during normal pauses)
      const connHealthy = !roomRef.current || validateRoomConnection();
      const transHealthy = transcriptionHealthRef.current?.isHealthy !== false;

      // Only check transcription health (resets timeout if recent transcript)
      monitorTranscriptionHealth();

      // Only log warnings when BOTH connection and transcription are unhealthy
      // This prevents noise during normal AI thinking/generation pauses
      if (!connHealthy && !transHealthy) {

      }
    }, 30000); // Check every 30 seconds (reduced from 10s to avoid log noise)

    return () => clearInterval(healthCheckInterval);
  }, [completed, validateRoomConnection, monitorTranscriptionHealth]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle scroll events for unified chat panel
  const handleUnifiedChatScroll = useCallback(() => {
    if (!userTranscriptScrollRef.current) return;
    const scrollContainer = userTranscriptScrollRef.current;
    const scrollTop = scrollContainer.scrollTop;
    const scrollHeight = scrollContainer.scrollHeight;
    const clientHeight = scrollContainer.clientHeight;
    const isAtBottom = scrollHeight - scrollTop <= clientHeight + 50;

    if (!isAtBottom && scrollTop < lastScrollTopRef.current) {
      // User scrolled up - pause auto-scroll for 5 seconds
      setIsUserScrolledUp(true);
      setIsUserScrolling(true);
      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
      }
      autoScrollTimeoutRef.current = setTimeout(() => {
        setIsUserScrolledUp(false);
        setIsUserScrolling(false);
        if (userTranscriptScrollRef.current) {
          userTranscriptScrollRef.current.scrollTo({
            top: userTranscriptScrollRef.current.scrollHeight,
            behavior: 'smooth',
          });
        }
      }, 5000); // 5-second cooldown
    } else if (isAtBottom) {
      // User scrolled back to bottom - resume auto-scroll immediately
      setIsUserScrolledUp(false);
      setIsUserScrolling(false);
      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
        autoScrollTimeoutRef.current = null;
      }
    }
    lastScrollTopRef.current = scrollTop;
  }, []);

  // Auto-scroll unified chat when new messages arrive
  // Smart scrolling: Only scroll if user is near bottom or hasn't manually scrolled up
  // Never fights with user scrolling - respects user intent
  useEffect(() => {
    if (!userTranscriptScrollRef.current || isUserScrolledUp) return;

    const scrollContainer = userTranscriptScrollRef.current;
    const scrollTop = scrollContainer.scrollTop;
    const scrollHeight = scrollContainer.scrollHeight;
    const clientHeight = scrollContainer.clientHeight;
    const isAtBottom = scrollHeight - scrollTop <= clientHeight + 100; // 100px threshold for "near bottom"

    // Only auto-scroll if user is near bottom (hasn't scrolled up)
    // This ensures we don't fight with user scrolling
    if (isAtBottom) {
      const el = userTranscriptScrollRef.current;
      // Use requestAnimationFrame for smooth scrolling
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (el && !isUserScrolledUp) {
            el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
          }
        }, 0);
      });
    }
  }, [transcript.length, partialTranscript, partialAITranscript, isUserScrolledUp]);

  // Cleanup timeout and refs on unmount
  // NOTE: Transcript state is NOT cleared - it persists for reconnect resilience
  useEffect(() => {
    return () => {
      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
      }
      // Clean up segment tracking (but keep transcript state for reconnect)
      processedSegmentIdsRef.current.clear();
      agentAudioTrackIdRef.current = null;
      lastPartialRoleRef.current = null;
      lastPartialSegmentIdRef.current = null;
      previousAISpeakingRef.current = false;
      // Reset streaming message indices (will be recreated on reconnect)
      streamingMessageIndexRef.current = { candidate: null, ai: null };
      // Reset current utterance tracking
      currentUtteranceIdRef.current = null;
    };
  }, []);

  // Calculate question number (placeholder - can be enhanced with actual question data from data channel)
  const questionNumber = Math.max(
    1,
    Math.floor(transcript.filter((t) => t.role === 'ai').length / 2) + 1
  );
  const maxQuestions = 10; // Placeholder - can be enhanced with actual data

  /**
   * Start interview
   */
  useEffect(() => {
    let sessionId, url, token;

    async function start() {
      // Guard against double initialization (React StrictMode)
      if (initializingRef.current || initializedRef.current) {
        logger.log('⏭️ Skipping duplicate initialization');
        return;
      }
      initializingRef.current = true;

      if (!sharingLink || !state?.name?.trim() || !state?.email?.trim()) {
        setError('Missing required information. Please go back and enter your details.');
        initializingRef.current = false;
        return;
      }

      try {
        // REMOVED: Waiting page logic - no longer using pre-connected rooms
        const selectedMicrophoneId = state?.selectedMicrophoneId;

        // Priority 1: Room from context (if available)
        if (getRoom && getUrl && getToken) {
          const existingRoom = getRoom();
          const existingUrl = getUrl();
          const existingToken = getToken();

          if (existingRoom && existingRoom.isConnected && existingUrl && existingToken) {
            logger.log('✅ Reusing existing Livekit connection from context (seamless)');

            // Use existing connection
            roomRef.current = existingRoom;
            url = existingUrl;
            token = existingToken;
            urlRef.current = url;
            tokenRef.current = token;

            // Get session info from state
            sessionId = state?.sessionId;
            sessionIdRef.current = sessionId;
            candidateNameRef.current = state?.name || 'Candidate';

            // If agent already ready from context, mark it
            if (contextAgentReady) {
              logger.log('✅ Agent already ready from context - will skip agent wait steps');
              agentReadyRef.current = true;
              agentJoinedRef.current = true;
              setAgentJoinOverdue(false);
            }

            // Skip connection - already connected!
            setStatus('Connected');
            setIsConnected(true);
            isConnectedRef.current = true;
            pastConnectionPhaseRef.current = true;

            // Setup event handlers on existing room
            setupRoomHandlers(existingRoom, sessionId);

            // Send clientReady signal for context-based room
            try {
              if (
                existingRoom &&
                existingRoom.localParticipant &&
                existingRoom.state === 'connected'
              ) {
                const encoder = new TextEncoder();
                const payload = encoder.encode(
                  JSON.stringify({
                    type: 'clientReady',
                    timestamp: Date.now(),
                  })
                );
                existingRoom.localParticipant.publishData(payload, {
                  reliable: true,
                  topic: 'interview-updates',
                });
                logger.log('✅ Client ready signal sent to agent (context room)');
              }
            } catch (err) {
            }
          } else if (state?.preInitializedLivekit && state?.livekitUrl && state?.livekitToken) {
            // Fallback: Use pre-obtained credentials (if context not available)
            logger.log('✅ Using pre-obtained Livekit credentials from state');

            url = state.livekitUrl;
            token = state.livekitToken;
            urlRef.current = url;
            tokenRef.current = token;

            sessionId = state?.sessionId;
            sessionIdRef.current = sessionId;
            candidateNameRef.current = state?.name || 'Candidate';

            if (state?.agentAlreadyReady) {
              logger.log('✅ Agent already ready - will skip agent wait steps');
              agentReadyRef.current = true;
            }

            setStatus('Connecting to LiveKit...');

            const room = new Room({
              autoSubscribe: true,
              adaptiveStream: true,
            });
            roomRef.current = room;
          } else {
            // Normal initialization flow
            setStatus('Starting interview session...');

            // 1. Request microphone permission first
            const hasPermission = await requestMicrophonePermission();
            if (!hasPermission) {
              return; // Error already set by handlePermissionDenial
            }

            // 2. Start interview session (if not already started)
            let candidateName = state?.name || 'Candidate';
            let candidateEmail = state?.email;

            if (!state?.sessionId) {
              if (!sharingLink || !candidateName || !candidateEmail) {
                setError('Missing required information. Please go back and enter your details.');
                return;
              }

              const sessionRes = await fetch(`${API_BASE_URL}/api/ai-interviews/session/start`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  sharingLink,
                  candidateName,
                  candidateEmail,
                }),
                signal: AbortSignal.timeout(30000),
              });

              if (!sessionRes.ok) {
                const errorData = await sessionRes.json().catch(() => ({}));
                throw new Error(errorData.message || 'Failed to start session');
              }

              const sessionData = await sessionRes.json();
              sessionId = sessionData.sessionId;
              candidateName = sessionData.candidateName || candidateName;
              // Store session access token for authenticated session calls
              if (sessionData.sessionToken) sessionTokenRef.current = sessionData.sessionToken;
            } else {
              sessionId = state.sessionId;
              candidateName = state?.name || 'Candidate';
              if (state?.sessionToken) sessionTokenRef.current = state.sessionToken;
            }

            // 🔴 FIX: Store sessionId and candidateName in refs for reconnection and completion navigation
            sessionIdRef.current = sessionId;
            candidateNameRef.current = candidateName;

            setStatus('Getting LiveKit token...');

            // 3. Get LiveKit token
            const lkHeaders = { 'Content-Type': 'application/json' };
            if (sessionTokenRef.current) lkHeaders['x-session-token'] = sessionTokenRef.current;

            // Waits in the lobby while every interviewer is busy.
            const tokenData = await fetchRoomToken(
              {
                sharingLink: sharingLink || state?.sharingLink,
                candidateName: state?.name ?? candidateName,
                candidateEmail: state?.email ?? candidateEmail,
                sessionId,
              },
              lkHeaders
            );
            if (tokenData.sessionToken) sessionTokenRef.current = tokenData.sessionToken;
            token = tokenData.token;
            url = tokenData.url;
            setInterviewerAvatar(tokenData.interviewerAvatar === true);
            // If we have sessionId + sessionToken, fetch and log agent prompt data in browser console (for debugging)
            const sid = tokenData.sessionId || sessionId;
            const stok = tokenData.sessionToken || sessionTokenRef.current;
            if (sid && stok && typeof window !== 'undefined') {
              fetch(`${API_BASE_URL}/api/ai-interviews/session/${sid}/agent-data`, {
                headers: { 'x-session-token': stok },
              })
                .then((r) => (r.ok ? r.json() : null))
                .then((data) => {
                  if (data) {

                  }
                })
                .catch(() => { });
            }

            // 🔴 FIX: Store url and token in refs for reconnection
            urlRef.current = url;
            tokenRef.current = token;

            // Validate URL and token before attempting connection
            if (!url) {
              throw new Error('LiveKit URL not configured');
            }

            // Validate URL format - must be wss:// for secure WebSocket
            if (!url.startsWith('wss://')) {

            }

            // 🔴 FIX ISSUE 1: Strip /rtc or /ws paths if present
            // LiveKit JS SDK adds the signaling path internally, so URL must end at domain
            const originalUrl = url;
            url = url.trim();

            // Remove trailing paths that LiveKit SDK will add
            if (url.endsWith('/rtc') || url.endsWith('/rtc/')) {

              url = url.replace(/\/rtc\/?$/, '');
            }
            if (url.endsWith('/ws') || url.endsWith('/ws/')) {

              url = url.replace(/\/ws\/?$/, '');
            }

            // Log exact URL for debugging (critical for diagnosing connection issues)
            logger.log('🔗 LIVEKIT URL EXACT:', url);
            logger.log('🔗 Original URL from backend:', originalUrl);
            if (originalUrl !== url) {
            }

            // Log connection details for debugging
            logger.log('🔗 Connecting to LiveKit:', {
              url: url.substring(0, 50) + '...', // Log partial URL for security
              urlProtocol: url.split('://')[0],
              urlDomain: url.split('://')[1]?.split('/')[0] || 'unknown',
              tokenLength: token?.length || 0,
              tokenPrefix: token?.substring(0, 20) + '...',
            });

            setStatus('Connecting to LiveKit...');

            // 4. Create new room (if not pre-initialized)
            const room = new Room({
              // CRITICAL: Enable auto-subscription to receive agent audio tracks automatically
              autoSubscribe: true,
              // Enable adaptive stream for better quality
              adaptiveStream: true,
            });
            roomRef.current = room;
          }
        } else if (state?.preInitializedLivekit && state?.livekitUrl && state?.livekitToken) {
          // Fallback: Use pre-obtained credentials (if context not available)
          logger.log('✅ Using pre-obtained Livekit credentials from state');

          url = state.livekitUrl;
          token = state.livekitToken;
          urlRef.current = url;
          tokenRef.current = token;

          sessionId = state?.sessionId;
          sessionIdRef.current = sessionId;
          candidateNameRef.current = state?.name || 'Candidate';

          if (state?.agentAlreadyReady) {
            logger.log('✅ Agent already ready - will skip agent wait steps');
            agentReadyRef.current = true;
          }

          setStatus('Connecting to LiveKit...');

          const room = new Room({
            autoSubscribe: true,
            adaptiveStream: true,
          });
          roomRef.current = room;
        } else {
          // Normal initialization flow (when coming directly without pre-test)
          setStatus('Starting interview session...');

          // 1. Request microphone permission first
          const hasPermission = await requestMicrophonePermission();
          if (!hasPermission) {
            return; // Error already set by handlePermissionDenial
          }

          // 2. Start interview session (if not already started)
          let candidateName = state?.name || 'Candidate';
          let candidateEmail = state?.email;

          if (!state?.sessionId) {
            if (!sharingLink || !candidateName || !candidateEmail) {
              setError('Missing required information. Please go back and enter your details.');
              return;
            }

            const sessionRes = await fetch(`${API_BASE_URL}/api/ai-interviews/session/start`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sharingLink,
                candidateName,
                candidateEmail,
              }),
              signal: AbortSignal.timeout(30000),
            });

            if (!sessionRes.ok) {
              const errorData = await sessionRes.json().catch(() => ({}));
              const errorCode = errorData.code || '';

              if (errorCode === 'ALREADY_COMPLETED') {
                setError('You have already completed this interview. Multiple attempts are not allowed.');
                return;
              }
              if (errorCode === 'QUOTA_EXCEEDED') {
                setError('Interview credits have been exhausted for this organisation. Please contact the recruiter.');
                return;
              }
              if (sessionRes.status === 429) {
                setError('Too many requests. Please wait a moment and refresh the page to try again.');
                return;
              }
              throw new Error(errorData.message || 'Failed to start session');
            }

            const sessionData = await sessionRes.json();
            sessionId = sessionData.sessionId;
            candidateName = sessionData.candidateName || candidateName;
            if (sessionData.sessionToken) sessionTokenRef.current = sessionData.sessionToken;
          } else {
            sessionId = state.sessionId;
            candidateName = state?.name || 'Candidate';
            if (state?.sessionToken) sessionTokenRef.current = state.sessionToken;
          }

          // 🔴 FIX: Store sessionId and candidateName in refs for reconnection and completion navigation
          sessionIdRef.current = sessionId;
          candidateNameRef.current = candidateName;

          setStatus('Getting LiveKit token...');

          // 3. Get LiveKit token
          const lkHeaders2 = { 'Content-Type': 'application/json' };
          if (sessionTokenRef.current) lkHeaders2['x-session-token'] = sessionTokenRef.current;

          // Waits in the lobby while every interviewer is busy; busy and
          // network errors never reach the error screen from here.
          let tokenData;
          try {
            tokenData = await fetchRoomToken(
              {
                sharingLink: sharingLink || state?.sharingLink,
                candidateName: state?.name ?? candidateName,
                candidateEmail: state?.email ?? candidateEmail,
                sessionId,
              },
              lkHeaders2
            );
          } catch (tokenErr) {
            if (tokenErr.code === 'ALREADY_COMPLETED') {
              setError('You have already completed this interview.');
              return;
            }
            throw tokenErr;
          }
          if (tokenData.sessionToken) sessionTokenRef.current = tokenData.sessionToken;
          token = tokenData.token;
          url = tokenData.url;

          // 🔴 FIX: Store url and token in refs for reconnection
          urlRef.current = url;
          tokenRef.current = token;

          // Validate URL and token before attempting connection
          if (!url) {
            throw new Error('LiveKit URL not configured');
          }

          // Validate URL format - must be wss:// for secure WebSocket
          if (!url.startsWith('wss://')) {

          }

          // 🔴 FIX ISSUE 1: Strip /rtc or /ws paths if present
          // LiveKit JS SDK adds the signaling path internally, so URL must end at domain
          const originalUrl = url;
          url = url.trim();

          // Remove trailing paths that LiveKit SDK will add
          if (url.endsWith('/rtc') || url.endsWith('/rtc/')) {

            url = url.replace(/\/rtc\/?$/, '');
          }
          if (url.endsWith('/ws') || url.endsWith('/ws/')) {

            url = url.replace(/\/ws\/?$/, '');
          }

          // Log exact URL for debugging (critical for diagnosing connection issues)
          logger.log('🔗 LIVEKIT URL EXACT:', url);
          logger.log('🔗 Original URL from backend:', originalUrl);
          if (originalUrl !== url) {
          }

          // Log connection details for debugging
          logger.log('🔗 Connecting to LiveKit:', {
            url: url.substring(0, 50) + '...', // Log partial URL for security
            urlProtocol: url.split('://')[0],
            urlDomain: url.split('://')[1]?.split('/')[0] || 'unknown',
            tokenLength: token?.length || 0,
            tokenPrefix: token?.substring(0, 20) + '...',
          });

          setStatus('Connecting to LiveKit...');

          // 4. Create new room (if not pre-initialized)
          const room = new Room({
            // CRITICAL: Enable auto-subscription to receive agent audio tracks automatically
            autoSubscribe: true,
            // Enable adaptive stream for better quality
            adaptiveStream: true,
          });
          roomRef.current = room;
        }

        // Get room reference (should be set in both paths above)
        const room = roomRef.current;
        if (!room) {
          throw new Error('Room not initialized');
        }

        // Parallelize: start mic track creation early so it runs alongside room.connect()
        setStatus('Requesting microphone access...');
        micPublishedRef.current = false;
        const micConstraints = {
          echoCancellation: true,
          noiseSuppression: false,
          autoGainControl: false,
        };
        if (selectedMicrophoneId && selectedMicrophoneId !== 'default') {
          micConstraints.deviceId = { exact: selectedMicrophoneId };
        }
        const createMicTrack = async () => {
          try {
            return await createLocalAudioTrack(micConstraints);
          } catch (err) {
            if (
              (err.name === 'OverconstrainedError' || err.name === 'NotFoundError') &&
              micConstraints.deviceId
            ) {
              delete micConstraints.deviceId;
              return await createLocalAudioTrack(micConstraints);
            }
            throw err;
          }
        };
        let micPromiseOrNull = null;

        // ✅ NEW: Only connect if not already connected (seamless transition)
        if (!room.isConnected) {
          connectingRef.current = true;
          try {
            micPromiseOrNull = createMicTrack();

            let connectUrl = url;
            let connectToken = token;
            const isRetryable = (err) =>
              err?.message &&
              (err.message.includes('Abort') ||
                err.message.includes('abort') ||
                err.message.includes('closed before the connection is established') ||
                err.message.includes('WebSocket') ||
                err.message.includes('Client initiated disconnect'));

            // 🔊 CRITICAL FIX: Register event handlers BEFORE room.connect() so that
            // no trackSubscribed / trackPublished events are missed during connection.
            // The LiveKit SDK allows registering listeners on a Room before connect().
            setupRoomHandlers(room, sessionId);

            const doConnect = async () => {
              await room.connect(connectUrl, connectToken);
            };

            try {
              await doConnect();
            } catch (firstError) {
              // If component was cleaned up during connect (e.g. effect re-run), don't surface error
              if (!roomRef.current) {
                logger.log('⏭️ Connect aborted (cleanup ran)');
                return;
              }
              // Retry once with a fresh token on transient WebSocket/signaling failures
              if (isRetryable(firstError)) {

                setStatus('Retrying connection...');
                try {
                  const retryHeaders = { 'Content-Type': 'application/json' };
                  if (sessionTokenRef.current) retryHeaders['x-session-token'] = sessionTokenRef.current;

                  const tokenRes = await fetch(
                    `${API_BASE_URL}/api/ai-interviews/session/livekit-token`,
                    {
                      method: 'POST',
                      headers: retryHeaders,
                      body: JSON.stringify({
                        sharingLink: sharingLink || state?.sharingLink,
                        candidateName: state?.name ?? candidateNameRef.current ?? 'Candidate',
                        candidateEmail: state?.email ?? state?.email,
                        sessionId,
                      }),
                    }
                  );
                  if (tokenRes.ok) {
                    const tokenData = await tokenRes.json();
                    connectToken = tokenData.token;
                    connectUrl = (tokenData.url || '').trim();
                    if (connectUrl.endsWith('/rtc') || connectUrl.endsWith('/rtc/')) {
                      connectUrl = connectUrl.replace(/\/rtc\/?$/, '');
                    }
                    if (connectUrl.endsWith('/ws') || connectUrl.endsWith('/ws/')) {
                      connectUrl = connectUrl.replace(/\/ws\/?$/, '');
                    }
                    tokenRef.current = connectToken;
                    urlRef.current = connectUrl;
                    await doConnect();
                  } else {
                    throw firstError;
                  }
                } catch (retryError) {
                  if (!roomRef.current) return;
                  throw retryError;
                }
              } else {
                throw firstError;
              }
            }

            logger.log('✅ Successfully connected to LiveKit room');
            setIsConnected(true);
            isConnectedRef.current = true;

            // 🔊 CRITICAL: Resume browser AudioContext so agent audio can play.
            // Browsers block autoplay until a user gesture; startAudio() creates/resumes
            // the AudioContext under the gesture that navigated to this page.
            try {
              await room.startAudio();
              logger.log('✅ room.startAudio() succeeded - browser AudioContext resumed');
            } catch (audioErr) {
              console.error('[AUDIO_START_FAIL]', audioErr.message);
            }

            // Mark initialization as complete (prevents double-init from StrictMode)
            initializedRef.current = true;
            // Store the stateKey used for this initialization
            initializedStateKeyRef.current = stateKey;
            pastConnectionPhaseRef.current = true;
          } catch (connectError) {
            if (!roomRef.current) {
              logger.log('⏭️ Connect failed after cleanup');
              return;
            }

            const hint =
              `LiveKit signaling connection failed. Check:\n` +
              `1. LIVEKIT_URL must be wss:// (not http:// or ws://)\n` +
              `2. URL must NOT include /rtc or /ws (LiveKit SDK adds this)\n` +
              `3. LIVEKIT_API_KEY and LIVEKIT_API_SECRET must match the LiveKit Cloud project for this URL\n` +
              `4. Check browser console Network tab → WS for WebSocket errors\n` +
              `5. Agent may not be ready - check agent service logs\n` +
              `Current URL: ${url}\n` +
              `URL protocol: ${url.split('://')[0]}\n` +
              `URL domain: ${url.split('://')[1]?.split('/')[0] || 'unknown'}`;
            if (
              connectError.message?.includes('Abort') ||
              connectError.message?.includes('abort') ||
              connectError.message?.includes('closed before')
            ) {
              throw new Error(hint);
            }
            throw connectError;
          } finally {
            connectingRef.current = false;
          }
        } else {
          // Already connected - just setup handlers
          logger.log('✅ Room already connected - reusing connection');
          setIsConnected(true);
          isConnectedRef.current = true;
          initializedRef.current = true;
          initializedStateKeyRef.current = stateKey;
          setupRoomHandlers(room, sessionId);
          pastConnectionPhaseRef.current = true;

          // 🔊 Resume AudioContext for pre-connected room too
          try {
            await room.startAudio();
            logger.log('✅ room.startAudio() succeeded (pre-connected room)');
          } catch (audioErr) {
            console.error('[AUDIO_START_FAIL]', audioErr.message);
          }
        }

        // Continue with rest of initialization (agent wait, mic setup, etc.)

        // 5. Get microphone track (already started in parallel with connect when applicable)
        try {
          const micTrack = micPromiseOrNull ? await micPromiseOrNull : await createMicTrack();
          micTrackRef.current = micTrack;
          attachMicTrackRecovery(micTrack);
          setIsMicMuted(false);
          setAgentBannerWarning(null);
        } catch (err) {

          // Permission-related errors → show the mic-permission UI
          if (err.name === 'NotAllowedError' || err.name === 'SecurityError') {
            handlePermissionDenial();
          } else {
            // Device/other errors → show a clearer message
            setError(
              'Could not access your microphone. Please check your audio device ' +
              'and browser permissions, then refresh the page.'
            );
          }
          return;
        }

        setIsListening(true);

        // Now publish mic track (agent is ready or timeout occurred)
        // 🔴 FIX: Use safe publish function that waits for room to be fully connected
        // This prevents "could not createOffer with closed peer connection" errors
        try {
          await publishMicrophoneTrack(room);
          logger.log('mic published immediately');
        } catch (err) {
          console.error('[MIC_PUBLISH_FAIL]', err.message);
        }

        // Publish camera track for video interviews
        if (isVideoInterview) {
          try {
            const videoTrack = await createLocalVideoTrack({
              resolution: { width: 640, height: 360 },
              facingMode: 'user',
            });
            localVideoTrackRef.current = videoTrack;
            if (localVideoRef.current) {
              videoTrack.attach(localVideoRef.current);
            }
            await room.localParticipant.publishTrack(videoTrack);
            logger.log('📹 Camera track published for video interview');
          } catch (camErr) {
            console.error('[CAM_PUBLISH_FAIL]', camErr.message);
          }
        }

        // 🔴 FIX: Set timeout to detect if agent never joins (60 seconds). Offer "Keep waiting" instead of hard error.
        agentJoinTimeoutRef.current = setTimeout(() => {
          if (!agentJoinedRef.current && !completedRef.current) {

            setAgentJoinOverdue(true);
          }
        }, 60000); // 60 seconds timeout

        // Set a timeout fallback (30 seconds) - if agent doesn't send ready signal
        agentReadyTimeoutRef.current = setTimeout(() => {
          if (!agentReadyRef.current && !completedRef.current && agentJoinedRef.current) {
            setInterviewReady(true);
            setStatus(INTERVIEW_READY_STATUS);
          }
        }, 30000);

        // Event handlers already set up above (moved before status is set to "Ready!")
        // Agent ready signal will be handled in dataReceived handler

        // REMOVED: clientReady is now sent immediately when agentReady signal is received
        // See dataReceived handler above for clientReady sending logic

        // 7. Setup mute abuse monitoring
        muteAbuseTimerRef.current = setInterval(() => {
          if (isMicMuted && !completed) {
            // Warn if muted for too long
            logger.log('Microphone is muted');
          }
        }, 5000);
      } catch (err) {
        if (err instanceof AdmissionCancelledError) {
          initializingRef.current = false;
          return;
        }
        if (err instanceof AdmissionFailedError) {
          // Could not get an interviewer: stay in the lobby design with "Try again".
          console.error('[INTERVIEW_ADMISSION_FAILED]', { sessionId: sessionIdRef.current, code: err.code });
          setAdmission({ mode: 'failed', message: err.message });
          initializingRef.current = false;
          return;
        }
        console.error('[INTERVIEW_FATAL]', { sessionId: sessionIdRef.current, error: err.message });
        let userMsg = err.message || 'Failed to start interview';
        if (err.name === 'TimeoutError' || err.name === 'AbortError') {
          userMsg = 'The request timed out. Please check your internet connection and refresh the page to try again.';
        }
        setError(userMsg);
        initializingRef.current = false;
      }
    }

    start();

    // Cleanup on unmount ONLY
    // 🔴 CRITICAL FIX: Only cleanup if component is actually unmounting OR stateKey changed
    // If stateKey changed, we need to re-initialize, so cleanup is OK
    // But we need to distinguish between "unmount" and "dependency change"
    return () => {
      // Check if stateKey changed (dependency change) vs component unmounting
      const stateKeyChanged =
        initializedStateKeyRef.current !== null && initializedStateKeyRef.current !== stateKey;

      if (initializedRef.current) {
        if (stateKeyChanged) {
          // StateKey changed - we're re-initializing with new state
          // Cleanup old connection but don't stop tracks (they'll be reused)
          logger.log('🔄 StateKey changed - cleaning up for re-initialization');
          cleanup(false); // Don't stop tracks, just disconnect room
          initializedStateKeyRef.current = null; // Reset for new init
        } else {
          // Component unmounting - full cleanup
          logger.log('🧹 Component unmounting - full cleanup');
          cleanup(true); // Stop tracks and disconnect
        }
      } else {
        // Never initialized - no cleanup needed
        logger.log('⏭️ Skipping cleanup - never initialized');
      }
      // Reset guards (allows re-init if component remounts or stateKey changes)
      initializingRef.current = false;
      initializedRef.current = false;
    };
    // 🔴 FIX: Removed 'completed' and 'isMicMuted' from dependencies
    // These state changes should NOT trigger re-initialization of the interview
    // When setIsMicMuted(false) is called after connection, it was causing
    // useEffect to re-run, which called cleanup() and disconnected the room
    //
    // 🔴 CRITICAL FIX: Removed callbacks from dependencies to prevent unnecessary re-runs
    // Callbacks (requestMicrophonePermission, handlePermissionDenial, setupRoomHandlers,
    // cleanup, publishMicrophoneTrack) are stable (useCallback with empty/stable dependencies)
    // and should NOT trigger re-initialization. Including them causes the effect to re-run
    // unnecessarily, calling cleanup() and disconnecting the room.
    // Only re-run when actual inputs (sharingLink or state values) change.
  }, [sharingLink, stateKey]);

  // Unified transcript: all messages sorted by timestamp (chronological order)
  // Includes both final and streaming messages - streaming messages update in place
  const unifiedTranscript = [...transcript].sort((a, b) => {
    const timeA =
      a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
    const timeB =
      b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
    return timeA - timeB;
  });

  // Waiting room: every interviewer busy, connection being retried, or gave up.
  if (admission && !error) {
    return (
      <InterviewWaitingLobby
        mode={admission.mode}
        position={admission.position}
        estimatedWaitMinutes={admission.estimatedWaitMinutes}
        message={admission.message}
        onRetry={() => window.location.reload()}
        onBack={() => navigate(-1)}
        t={t}
      />
    );
  }

  // Error display
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {micPermissionDenied ? t('Microphone Permission Required') : t('Connection Error')}
          </h2>
          <p className="text-gray-600 mb-4">{translateInterviewMessage(t, error)}</p>
          <div className="flex gap-2 justify-center flex-wrap">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              {t('Go Back')}
            </button>
            {error === RECONNECT_FAILED_MESSAGE && (
              <button
                onClick={handleRetryReconnect}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                {t('Try again')}
              </button>
            )}
            {micPermissionDenied && (
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                {t('Retry')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Main interview UI - Ribbon AI Style Layout
  return (
    <>
      {agentBannerWarning && (
        <div
          className="fixed top-0 left-0 right-0 z-[200] flex justify-center px-3 pt-3 pointer-events-none"
          role="status"
        >
          <div className="pointer-events-auto flex max-w-lg items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 shadow-lg">
            <span className="text-amber-600 text-lg leading-none" aria-hidden>
              ⚠️
            </span>
            <p className="flex-1 text-sm text-amber-950">{translateInterviewMessage(t, agentBannerWarning)}</p>
            <button
              type="button"
              onClick={() => setAgentBannerWarning(null)}
              className="shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100"
            >
              {t('Dismiss')}
            </button>
          </div>
        </div>
      )}
      <LeaveScreeningModal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        onStay={() => setShowLeaveModal(false)}
        onLeave={() => { setShowLeaveModal(false); navigate(-1); }}
        title={t('Leave this page?')}
        message={t('Refreshing or closing this page may auto-submit your assessment and affect your progress. Please stay on this page until you complete this step or your time runs out.')}
      />
      <style>{`
        .transcript-scroll {
          scroll-behavior: smooth;
        }
        .transcript-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .transcript-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .transcript-scroll::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.1);
          border-radius: 4px;
        }
        .transcript-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.2);
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.6; }
          25% { transform: translate(20px, -30px) scale(1.1); opacity: 0.7; }
          50% { transform: translate(-20px, 20px) scale(0.9); opacity: 0.5; }
          75% { transform: translate(30px, 25px) scale(1.05); opacity: 0.65; }
        }
        @keyframes colorShift {
          0% { background: linear-gradient(135deg, #a855f7, #ec4899, #3b82f6); }
          33% { background: linear-gradient(135deg, #ec4899, #3b82f6, #a855f7); }
          66% { background: linear-gradient(135deg, #3b82f6, #a855f7, #ec4899); }
          100% { background: linear-gradient(135deg, #a855f7, #ec4899, #3b82f6); }
        }
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes borderGradient {
          0% { border-color: #a855f7; box-shadow: 0 0 20px rgba(168, 85, 247, 0.3); }
          33% { border-color: #ec4899; box-shadow: 0 0 20px rgba(236, 72, 153, 0.3); }
          66% { border-color: #3b82f6; box-shadow: 0 0 20px rgba(59, 130, 246, 0.3); }
          100% { border-color: #a855f7; box-shadow: 0 0 20px rgba(168, 85, 247, 0.3); }
        }
        @keyframes smoothPulse {
          0%, 100% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(1.05); opacity: 0.8; }
        }
        @keyframes smoothRotate {
          0% { transform: rotate(0deg) scale(1); }
          100% { transform: rotate(360deg) scale(1); }
        }
        .animate-gradient-shift {
          background: linear-gradient(135deg, #a855f7, #ec4899, #3b82f6, #a855f7);
          background-size: 300% 300%;
          animation: gradientShift 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          will-change: background-position;
        }
        .animate-border-gradient {
          animation: borderGradient 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          will-change: border-color, box-shadow;
        }
        .animate-blob-color-shift {
          animation: colorShift 5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          will-change: background;
        }
        .animate-smooth-pulse {
          animation: smoothPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          will-change: transform, opacity;
        }
        .animate-smooth-rotate {
          animation: smoothRotate 8s linear infinite;
          will-change: transform;
        }
        @keyframes preparingOverlayFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-preparing-overlay {
          animation: preparingOverlayFadeIn 0.35s ease-out forwards;
        }
        @keyframes preparingContentFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-preparing-content {
          animation: preparingContentFadeIn 0.3s ease-out 0.1s forwards;
        }
        @keyframes auraPanelEnter {
          from { opacity: 0; transform: scale(0.82); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-aura-panel-enter {
          animation: auraPanelEnter 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
          will-change: transform, opacity;
        }
        @keyframes videoPanelEnter {
          from { opacity: 0; transform: translateX(24px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .animate-video-panel-enter {
          animation: videoPanelEnter 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.12s both;
          will-change: transform, opacity;
        }
        @keyframes auraPanelEnter {
          from { opacity: 0; transform: scale(0.82); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-aura-panel-enter {
          animation: auraPanelEnter 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
          will-change: transform, opacity;
        }
        @keyframes videoPanelEnter {
          from { opacity: 0; transform: translateX(24px) scale(0.96); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        .animate-video-panel-enter {
          animation: videoPanelEnter 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.12s both;
          will-change: transform, opacity;
        }
      `}</style>
      {/* Preparing overlay: full-screen until interview status is "ready" */}
      {showPreparingOverlay && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm animate-preparing-overlay"
          role="dialog"
          aria-modal="true"
          aria-live="polite"
          aria-label={t('Preparing interview')}
        >
          <div className="animate-preparing-content opacity-0 mx-4 max-w-sm w-full rounded-2xl bg-white shadow-xl border border-slate-200/80 p-8 text-center">
            {agentLostMidInterview ? (
              <>
                <p className="text-slate-700 text-base sm:text-lg font-medium leading-snug">
                  {t('Your interviewer dropped out for a moment. Reconnecting you now — please stay on this page.')}
                </p>
                <p className="text-slate-500 text-sm mt-3 leading-snug">
                  {t('Your answers so far are saved. The interview will carry on from where it stopped.')}
                </p>
                <div
                  className="w-10 h-10 border-2 border-slate-200 border-t-primary-500 rounded-full animate-spin mx-auto mt-6"
                  aria-hidden="true"
                />
              </>
            ) : agentJoinOverdue ? (
              <>
                <p className="text-slate-700 text-base sm:text-lg font-medium leading-snug">
                  {t('This is taking longer than expected. You can keep waiting or try refreshing.')}
                </p>
                <div className="flex flex-col gap-2 mt-6">
                  <button
                    type="button"
                    onClick={handleAgentJoinKeepWaiting}
                    className="w-full px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium transition-colors"
                  >
                    {t('Keep waiting')}
                  </button>
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-medium transition-colors"
                  >
                    {t('Refresh & retry')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-slate-700 text-base sm:text-lg font-medium leading-snug">
                  {t("We're preparing your interview. Please wait a moment before we begin.")}
                </p>
                <div
                  className="w-10 h-10 border-2 border-slate-200 border-t-primary-500 rounded-full animate-spin mx-auto mt-6"
                  aria-hidden="true"
                />
              </>
            )}
          </div>
        </div>
      )}
      <div className="relative h-screen flex items-center justify-center p-3 md:p-4 bg-white overflow-hidden">
        {/* Home-screen style centered blue wash gradient */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            background:
              'radial-gradient(circle 550px at 50% 50%, #bfdbfe 0%, #dbeafe 30%, #eff6ff 60%, #ffffff 100%)',
          }}
        />
        {/* Banner: agent didn't join in time — offer Keep waiting / Leave */}
        {agentJoinOverdue && (
          <div className="absolute top-4 left-4 right-4 z-50 max-w-2xl mx-auto bg-amber-50 border border-amber-200 rounded-lg shadow-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-amber-900">
              {t("We're still connecting to your interviewer. This is taking longer than expected. You can keep waiting, or try refreshing the page. If the issue continues, please try again in a few minutes.")}
            </p>
            <div className="flex gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={handleAgentJoinKeepWaiting}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
              >
                {t('Keep waiting')}
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-white border border-amber-300 text-amber-800 rounded-lg hover:bg-amber-50 text-sm font-medium"
              >
                {t('Refresh & retry')}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
              >
                {t('Leave interview')}
              </button>
            </div>
          </div>
        )}
        {agentAudioNotReceived && !agentJoinOverdue && (
          <div className="absolute top-4 left-4 right-4 z-40 max-w-2xl mx-auto bg-blue-50 border border-blue-200 rounded-lg shadow p-3">
            <p className="text-sm text-blue-900">
              <strong>{t('No audio from interviewer yet.')}</strong>{' '}
              {t("Your browser is connected but we haven't received any sound. Please ensure your speaker volume is up and try waiting a few more seconds. If this continues, try refreshing the page.")}
            </p>
          </div>
        )}
        {/* Fullscreen Request Loading Overlay */}
        {isRequestingFullscreen && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 max-w-sm mx-4 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('Entering Fullscreen Mode')}</h3>
              <p className="text-sm text-gray-600">{t('Please wait while we set up your secure interview environment...')}</p>
            </div>
          </div>
        )}

        {/* Main Container - structured into top/status, stage, and controls */}
        <div className="relative z-10 w-full max-w-5xl h-full flex flex-col px-2 md:px-0">
          {/* Proctoring Indicator - Top Right */}
          <ProctoringIndicator
            isFullscreen={isFullscreen}
            proctoringEvents={proctoringEvents}
            shiftClickCount={shiftClickCount}
            rightClickCount={rightClickCount}
            isVisible={isConnected && !completed}
          />

          {/* Stage: symmetric two-panel layout for video interviews, single-aura for voice */}
          <div className="flex-1 flex flex-col items-center justify-center min-h-0 py-0 -translate-y-6 md:-translate-y-8">
            {micPermissionDenied ? (
              <div className="flex items-center justify-center animate-fadeIn p-4">
                <div className="text-center">
                  <p className="text-sm font-semibold text-slate-800 mb-2">
                    {t('Microphone required')}
                  </p>
                  <p className="text-xs text-slate-600">
                    {t('Allow microphone access to continue.')}
                  </p>
                </div>
              </div>
            ) : isVideoInterview ? (
              <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-14 lg:gap-20 w-full max-w-5xl">
                {/* Left panel — AI Aura (no background, sits on ambient gradient) */}
                <div className="animate-aura-panel-enter flex items-center justify-center w-full md:w-auto">
                  <div className="w-full flex flex-col items-center">
                    <div className="aspect-square w-[min(72vmin,420px)] md:w-[min(40vmin,420px)] flex items-center justify-center">
                    <InterviewerAvatar
                      gender={avatarGender}
                      speaking={avatarSpeaking}
                      className="h-full w-full rounded-3xl bg-white border-[6px] border-white shadow-[0_20px_50px_-12px_rgba(15,23,42,0.18)] ring-1 ring-slate-200/60"
                      fallback={
                        <AgentAudioVisualizerAura
                          size="xl"
                          state={getVisualizerState()}
                          audioTrack={agentAudioTrackRef.current}
                          color="#007BFF"
                          colorShift={0.6}
                          themeMode="light"
                          className="!h-full !w-full !min-h-0 !min-w-0 !rounded-xl"
                        />
                      }
                    />
                  </div>
                  </div>
                </div>

                {/* Right panel — Candidate video (white card, clean white border, matching dimensions) */}
                <div className="animate-video-panel-enter flex items-center justify-center w-full md:w-auto">
                  <div className="w-full flex flex-col items-center">
                    <div className="aspect-square w-[min(72vmin,420px)] md:w-[min(40vmin,420px)] rounded-3xl bg-white border-[6px] border-white shadow-[0_20px_50px_-12px_rgba(15,23,42,0.18)] overflow-hidden ring-1 ring-slate-200/60">
                      <video
                        ref={localVideoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover"
                        style={{ transform: 'scaleX(-1)' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="animate-aura-panel-enter w-full max-w-xl mx-auto flex items-center justify-center">
                <div className="aspect-square w-full max-w-[min(72vmin,420px)] flex items-center justify-center">
                  <InterviewerAvatar
                    gender={avatarGender}
                    speaking={avatarSpeaking}
                    className="h-full w-full rounded-3xl bg-white border-[6px] border-white shadow-[0_20px_50px_-12px_rgba(15,23,42,0.18)] ring-1 ring-slate-200/60"
                    fallback={
                      <AgentAudioVisualizerAura
                        size="xl"
                        state={getVisualizerState()}
                        audioTrack={agentAudioTrackRef.current}
                        color="#007BFF"
                        colorShift={0.6}
                        themeMode="light"
                        className="!h-full !w-full !min-h-0 !min-w-0 !rounded-xl"
                      />
                    }
                  />
                </div>
              </div>
            )}
          </div>

          {/* Control Bar - Bottom (Timer + Mic, Camera, Chat, End); tighter on mobile */}
          {/* Only render AgentControlBar when room exists - use RoomContext.Provider so useLiveKitRoom never runs (it would call room.disconnect() on connect=false and on unmount) */}
          <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 w-full max-w-md px-2 md:px-0">
            {roomRef.current ? (
              <RoomContext.Provider value={roomRef.current}>
                <AgentControlBar
                  variant="livekit"
                  isConnected={stableControlBarState.isConnected}
                  elapsedTime={elapsedTime}
                  formatTime={formatTime}
                  isChatOpen={isChatPanelVisible}
                  onIsChatOpenChange={setIsChatPanelVisible}
                  controls={{
                    leave: true,
                    microphone: true,
                    screenShare: false,
                    camera: true,
                    chat: false,
                  }}
                  cameraDisabled={true}
                  hideChatInput={true}
                  onDisconnect={() => {
                    logger.log('Candidate ended the call (button click)');
                    addProctoringEvent('CANDIDATE_ENDED_CALL', {
                      timestamp: new Date().toISOString()
                    });
                    setCompleted(true);
                    completedRef.current = true;
                    if (roomRef.current) {
                      roomRef.current.disconnect();
                    }
                    navigateToCompletion();
                  }}
                  className="w-full border-0 shadow-xl shadow-slate-900/10 ring-1 ring-slate-900/5"
                />
              </RoomContext.Provider>
            ) : (
              // Show placeholder control bar UI while room is connecting
              // This maintains layout stability without requiring LiveKit hooks
              <div
                className="bg-background flex flex-col p-2 rounded-2xl md:rounded-[31px] opacity-90 w-full md:p-3 border-0 shadow-xl shadow-slate-900/10 ring-1 ring-slate-900/5"
                role="toolbar"
                aria-label={t('Voice assistant controls (connecting...)')}
              >
                <div className="flex gap-0.5 md:gap-1 items-center">
                  {typeof elapsedTime === 'number' && formatTime && (
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-muted/50 border border-border/50 md:gap-2 md:px-3 md:py-1.5 flex-shrink-0">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-red-500 flex-shrink-0" />
                      <span className="text-xs font-mono text-foreground font-medium tabular-nums md:text-sm">
                        {formatTime(elapsedTime)}
                      </span>
                    </div>
                  )}
                  <div className="flex grow gap-0.5 md:gap-1">
                    {/* Placeholder controls - disabled state */}
                    <div className="px-2 py-1.5 md:px-3 md:py-2 rounded-full bg-muted/50 border border-border/50 opacity-50 cursor-not-allowed">
                      <span className="text-[11px] md:text-xs">{t('Connecting...')}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      logger.log('Candidate ended the call (button click - connecting)');
                      addProctoringEvent('CANDIDATE_ENDED_CALL', {
                        timestamp: new Date().toISOString()
                      });
                      setCompleted(true);
                      completedRef.current = true;
                      if (roomRef.current) {
                        roomRef.current.disconnect();
                      }
                      navigateToCompletion();
                    }}
                    className="bg-destructive/10 dark:bg-destructive/10 text-destructive hover:bg-destructive/20 dark:hover:bg-destructive/20 rounded-full font-mono text-[11px] md:text-xs font-bold tracking-wider px-3 py-1.5 md:px-4 md:py-2 flex-shrink-0"
                    data-testid="end-call-btn"
                    aria-label={t('End call (connecting)')}
                  >
                    <span className="hidden md:inline">{t('END CALL')}</span>
                    <span className="inline md:hidden">{t('END')}</span>
                  </button>
                </div>
              </div>
            )}
            <div className="w-full flex flex-col items-center justify-center mt-6 md:mt-7">
              <span className="text-[10px] md:text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400 mb-1">
                {t('Powered by')}
              </span>
              <div className="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 160 160"
                  aria-label="TalentCrux logo"
                  className="h-5 w-auto md:h-6"
                >
                  <path
                    fill="#007BFF"
                    d="M79.95,62.9h-29.76c-7.01,0-12.7-5.68-12.7-12.7h0c0-7.01,5.68-12.7,12.7-12.7h29.76c7.01,0,12.7,5.68,12.7,12.7h0c0,7.01-5.68,12.7-12.7,12.7ZM110.47,37.51c-7.15-.27-13.02,5.44-13.02,12.51-.07,6.12-3.26,11.48-8.07,14.56,0,0-.02,0-.03,0-2.65,1.7-5.79,2.7-9.16,2.75-7.11.1-12.94,5.6-12.94,12.71v29.75c0,3.51,1.42,6.68,3.71,8.97,2.27,2.27,5.4,3.68,8.86,3.71,7.05.06,12.82-6.12,12.82-13.17v-29.27c0-5.21,2.25-9.89,5.82-13.08.02-.03.04-.04.06-.05,2.66-2.38,6.06-3.93,9.8-4.3.26-.02.52-.04.77-.05.3,0,.59-.02.89-.02,7.07,0,12.78-5.86,12.51-13.02-.24-6.45-5.58-11.79-12.03-12.03Z"
                  />
                </svg>
                <span className="text-sm md:text-base font-extrabold tracking-tight text-slate-900">
                  TalentCrux
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* When completed, show brief redirect state */}
        {completed && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center">
            <p className="text-slate-600">{t('Taking you to the completion page...')}</p>
          </div>
        )}
      </div>
    </>
  );
}
